const typename = Symbol("typename");
export type Nominal<T, N extends string> = T & {readonly [typename]: N};
export function nominate<T, const N extends string>(_name: N, arg: T): Nominal<T, N> {
  return arg as Nominal<T, N>;
}
export type ApiDate = Nominal<string, "date">;
export type ApiDateTime = Nominal<string, "datetime">;
export type Tz = Nominal<string, "tz">;
export type ConfluenceDocumentSourceLabels = {[category in DocumentCategory]?: ConfluenceLabel};
export type BTreeMap<K extends string | number, V> = {[key in K]?: V};
export type IObject = {readonly [key: string]: IValue};
export type IValue = null | boolean | number | string | readonly IValue[] | IObject;
export type GraphOperationEntityRef = string | number;
export type Uuid = Nominal<string, "uuid">;
export type UserId = Nominal<string, "userId">;
export type OwnerId = Nominal<string, "ownerId">;
export type QuestionId = Nominal<string, "questionId">;
export type QuestionGroupId = Nominal<string, "questionGroupId">;
export type SectionId = Nominal<string, "sectionId">;
export type QuestionnaireId = Nominal<string, "questionnaireId">;
export type AccountId = Nominal<string, "accountId">;
export type CounterpartyId = Nominal<string, "counterpartyId">;
export type DocumentId = Nominal<string, "documentId">;
export type FileId = Nominal<string, "fileId">;
export type StandaloneFileId = Nominal<string, "standaloneFileId">;
export type DocumentationId = Nominal<string, "documentationId">;
export type TeamId = Nominal<string, "teamId">;
export type RoleId = Nominal<string, "roleId">;
export type DealId = Nominal<string, "dealId">;
export type ExternalAuthorizationId = Nominal<string, "externalAuthorizationId">;
export type AssetId = Nominal<string, "assetId">;
export type ActivityStreamId = Nominal<string, "activityStreamId">;
export type BackgroundTaskId = Nominal<string, "backgroundTaskId">;
export type ApikeyId = Nominal<string, "apikeyId">;
export type FactTextId = Nominal<string, "factTextId">;
export type FactClusterId = Nominal<string, "factClusterId">;
export type ScopeId = Nominal<string, "scopeId">;
export type ScopeAxisId = Nominal<string, "scopeAxisId">;
export type EntityId = Nominal<string, "entityId">;
export type ContentId = Nominal<string, "contentId">;
export type ResolutionId = Nominal<string, "resolutionId">;
export type ErrorCatalogId = Nominal<string, "errorCatalogId">;
export type DocumentAccessRequestId = Nominal<string, "documentAccessRequestId">;
export type CrmConnectionId = Nominal<string, "crmConnectionId">;
export type DocumentSourceId = Nominal<string, "documentSourceId">;
export type EsignatureProviderId = Nominal<string, "esignatureProviderId">;
export type EsignatureTemplateId = Nominal<string, "esignatureTemplateId">;
export type NotificationSinkId = Nominal<string, "notificationSinkId">;
export type QuestionSourceId = Nominal<string, "questionSourceId">;
export type LibrarySectionId = Nominal<string, "librarySectionId">;
export type FactId = Nominal<string, "factId">;
export type CoreResponseId = Nominal<string, "coreResponseId">;
export type FrameworkId = Nominal<string, "frameworkId">;
export type FrameworkVersionId = Nominal<string, "frameworkVersionId">;
export type FrameworkProfileId = Nominal<string, "frameworkProfileId">;
export type ThirdPartyId = Nominal<string, "thirdPartyId">;
export type AssessmentId = Nominal<string, "assessmentId">;
export type ControlId = Nominal<string, "controlId">;
export type ControlGroupId = Nominal<string, "controlGroupId">;
export type SourceId = Nominal<string, "sourceId">;
export type SourceChunkId = Nominal<string, "sourceChunkId">;
export type MailingListId = Nominal<string, "mailingListId">;
export type ShowcaseUpdateMessageId = Nominal<string, "showcaseUpdateMessageId">;
export type MailSubscriptionId = Nominal<string, "mailSubscriptionId">;
export type ThirdPartyTierId = Nominal<string, "thirdPartyTierId">;
export type ThirdPartyTagId = Nominal<string, "thirdPartyTagId">;
export type TprmScopeId = Nominal<string, "scopeId">;
export type RiskId = Nominal<string, "riskId">;
export type RiskAreaId = Nominal<string, "riskAreaId">;
export type RiskAreaExampleRiskId = Nominal<string, "riskAreaExampleRiskId">;
export type InAppNotificationId = Nominal<string, "inAppNotificationId">;
export type TprmQuestionnaireId = Nominal<string, "tprmQuestionnaireId">;
export type TprmExternalQuestionnaireId = Nominal<string, "tprmExternalQuestionnaireId">;
export type TprmQuestionTemplateId = Nominal<string, "tprmQuestionTemplateId">;
export type TprmSectionTemplateId = Nominal<string, "tprmSectionTemplateId">;
export type TprmQuestionnaireTemplateId = Nominal<string, "tprmQuestionnaireTemplateId">;
export type ControlEvidenceId = Nominal<string, "controlEvidenceId">;
export type RiskContextId = Nominal<string, "riskContextId">;
export type TprmReportTemplateId = Nominal<string, "tprmReportTemplateId">;
export type ScimConfigId = Nominal<string, "scimConfigId">;
export type VerifiedDomainId = Nominal<string, "verifiedDomainId">;
export type LoginMethodId = Nominal<string, "loginMethodId">;
export type TprmAssetId = Nominal<string, "tprmAssetId">;
export type OperationalContextId = Nominal<string, "operationalContextId">;
export type OperationalContextQuestionId = Nominal<string, "operationalContextQuestionId">;
export type ReviewActionId = Nominal<string, "reviewId">;
export type ReviewableId = Nominal<string, "reviewSetId">;
export type UserApikeyId = Nominal<string, "userApikeyId">;
export type FeedEventId = Nominal<number, "feedEventId">;
export type AssessmentNodeId = Nominal<number, "assessmentNodeId">;
export type DataRoomId = Nominal<string, "dataRoomId">;
export type ExternalAccountReferenceId = Nominal<string, "dataRoomId">;

export type PromptModifiers = {[K in PromptModifierType]?: (PromptModifier & {type: `${K}`})["content"]};
export type User = RegisteredUser | ExternalUser | ServiceUser;

type OrderedFloatF64 = number;
type UserTypeRegistered = UserType.Registered;
type UserTypeService = UserType.Service;
type UserTypeExternal = UserType.External;
type UserTypeSystemAgent = UserType.SystemAgent;
type Never = never;
type BTreeSet<T> = Array<T>;
type HashSet<T> = Array<T>;

// Stricter typing for assessment nodes

export type AssessmentDecision = AssessmentDecisionContent & {
  created: Actioned;
  was_suggested: boolean;
};

export interface TypedAssessmentNode<T extends `${AssessmentDecisionNodeType}` = `${AssessmentDecisionNodeType}`>
  extends AssessmentNode {
  node: AssessmentDecisionNode & {type: T};
  latest_decision?: TypedAssessmentDecision<T>;
  suggested_change?: TypedAssessmentDecision<T>;
}

export type TypedAssessmentDecision<T extends `${AssessmentDecisionNodeType}` = `${AssessmentDecisionNodeType}`> =
  AssessmentDecision & {type: T};

type IdentifiedRiskAssessmentNode = TypedAssessmentNode<"IdentifiedRisk">;
type InherentRiskAssessmentNode = TypedAssessmentNode<"InherentRisk">;
type RiskRelevantControlsAssessmentNode = TypedAssessmentNode<"RiskRelevantControls">;
type ResidualRiskAssessmentNode = TypedAssessmentNode<"ResidualRisk">;
type RiskAcceptanceAssessmentNode = TypedAssessmentNode<"RiskAcceptance">;
type ControlScopeSelectionAssessmentNode = TypedAssessmentNode<"ControlScopeSelection">;
type ControlScopeAssessmentAssessmentNode = TypedAssessmentNode<"ControlScopeAssessment">;
type ControlAssessmentAssessmentNode = TypedAssessmentNode<"ControlAssessment">;

// Everything below is generated by the rust/types build script
/*
 Generated by typeshare 1.11.0
*/

export enum AiFeature {
	QuestionAnalysis = "QuestionAnalysis",
	DocumentAnalysis = "DocumentAnalysis",
	AnswerGeneration = "AnswerGeneration",
	FactClustering = "FactClustering",
	ConflictDetection = "ConflictDetection",
	ReviewAnswerRelevance = "ReviewAnswerRelevance",
	ReviewAnswerAccuracy = "ReviewAnswerAccuracy",
	ReviewAnswerClarity = "ReviewAnswerClarity",
	ReviewAnswerCommercial = "ReviewAnswerCommercial",
	ReviewSummary = "ReviewSummary",
	ReviewAnswerSupportedByFact = "ReviewAnswerSupportedByFact",
	ReportSummary = "ReportSummary",
	ControlSourceSummary = "ControlSourceSummary",
	RiskAreaSourceSummary = "RiskAreaSourceSummary",
	ControlAssessment = "ControlAssessment",
	RiskControlMapping = "RiskControlMapping",
	RiskDeduplication = "RiskDeduplication",
	GenerativeRiskDiscovery = "GenerativeRiskDiscovery",
	RiskStability = "RiskStability",
	RiskQuantification = "RiskQuantification",
	ImageInterpretation = "ImageInterpretation",
}

export enum AiModel {
	Gpt4Turbo = "Gpt4Turbo",
	Gpt4oStructured = "Gpt4oStructured",
	Gpt4oMini = "Gpt4oMini",
	Claude3o5SonnetV2 = "Claude3o5SonnetV2",
	Claude3o5Haiku = "Claude3o5Haiku",
	Claude3Opus = "Claude3Opus",
	Claude3Haiku = "Claude3Haiku",
	Gemini1o5Pro = "Gemini1o5Pro",
	Gemini1o5Flash = "Gemini1o5Flash",
}

export type AiModelConfig = Record<AiFeature, AiModel>;

export type Region = string;

export type Release = string | undefined;

export enum WatcherTrigger {
	VTQuestionAsked = "VTQuestionAsked",
	VTQuestionCommented = "VTQuestionCommented",
	VTQuestionMentioned = "VTQuestionMentioned",
	VTQuestionAssigned = "VTQuestionAssigned",
	VTQuestionReviewed = "VTQuestionReviewed",
	VTQuestionReviewRequested = "VTQuestionReviewRequested",
}

/** List of actions which will cause the user to become a "watcher" of a resource */
export type WatcherPreferences = Record<WatcherTrigger, boolean>;

export enum AccessScope {
	TrustCenter = "TrustCenter",
	Collect = "Collect",
}

export interface AccessToken {
	token: string;
	user_id: UserId;
	client_account_id: AccountId;
	affiliated_account_id?: AccountId;
	expiry: ApiDateTime;
	scope: AccessScope;
}

export enum AccountType {
	Live = "Live",
	Sandbox = "Sandbox",
}

export enum AccountFeature {
	VendorToolkit = "VendorToolkit",
	TPRM = "TPRM",
	TrustCenter = "TrustCenter",
	Scopes = "Scopes",
	ExpressCredits = "ExpressCredits",
	CsvImport = "CsvImport",
	InstantAnswering = "InstantAnswering",
	ResponseWizard = "ResponseWizard",
	SpreadsheetImport = "SpreadsheetImport",
	MeilisearchFactLibrary = "MeilisearchFactLibrary",
	MeilisearchQuestionAnswering = "MeilisearchQuestionAnswering",
	Collect = "Collect",
	ContextualRiskAssessment = "ContextualRiskAssessment",
	ContextualPredefinedRisks = "ContextualPredefinedRisks",
	InstantAssessment = "InstantAssessment",
	ImageInterpretation = "ImageInterpretation",
	Scim = "Scim",
	SingleSignOn = "SingleSignOn",
	Review = "Review",
	ReviewAnswerRelevance = "ReviewAnswerRelevance",
	ReviewAnswerAccuracy = "ReviewAnswerAccuracy",
	ReviewAnswerSupportedByFact = "ReviewAnswerSupportedByFact",
	ReviewAnswerClarity = "ReviewAnswerClarity",
	ReviewAnswerCommercial = "ReviewAnswerCommercial",
}

export interface Account {
	account_id: AccountId;
	name: string;
	display_name: string;
	slug: string;
	region: Region;
	type_: AccountType;
	plan_features: AccountFeature[];
	enabled_features: AccountFeature[];
	prompt_modifiers: PromptModifiers;
	express_credits: number;
	invalidation_channel_name: string;
	facts_modified_at: ApiDateTime;
	facts_clustered_at: ApiDateTime;
	language_code: string;
	ai_model_config: AiModelConfig;
}

export interface AccountMin {
	account_id: AccountId;
	type_: AccountType;
	display_name: string;
	slug: string;
	region: Region;
}

export type LoginMethodInnerMin = 
	| { type: "UsernamePassword", content: EmptyLoginMethodPayload }
	| { type: "GoogleOAuth", content: EmptyLoginMethodPayload }
	| { type: "MicrosoftOAuth", content: EmptyLoginMethodPayload }
	| { type: "GenericOauth", content: EmptyLoginMethodPayload }
	| { type: "GenericSaml", content: EmptyLoginMethodPayload };

export interface LoginMethodMin {
	login_method_id: LoginMethodId;
	name: string;
	inner: LoginMethodInnerMin;
}

export interface AccountRestriction {
	login_method: LoginMethodMin;
	require_2fa: boolean;
}

export enum SystemRole {
	Admin = "Admin",
}

export interface RoleMin {
	role_id: RoleId;
	system_role?: SystemRole;
	name: string;
}

export interface AccountUser {
	user: User;
	roles: RoleMin[];
}

export interface AccountWithRestriction {
	account: AccountMin;
	restriction?: AccountRestriction;
}

export enum UserType {
	External = "External",
	Registered = "Registered",
	Service = "Service",
	SystemAgent = "SystemAgent",
}

export interface UserMin {
	user_id: UserId;
	type_: UserType;
	name?: string;
	primary_email?: string;
	avatar_url?: string;
}

export interface Actioned {
	at: ApiDateTime;
	by?: UserMin;
}

export type ActivityPayload = 
	| { type: "Comment", content: CommentPayload }
	| { type: "QuestionCreated", content: QuestionCreatedPayload }
	| { type: "QuestionUpdated", content: QuestionUpdatedPayload }
	| { type: "DocumentUpdated", content: DocumentUpdatedPayload }
	| { type: "AssessedControlUpdated", content: AssessedControlUpdatedPayload }
	| { type: "RiskUpdated", content: RiskUpdatedPayload }
	| { type: "Review", content: ReviewPayload };

export type Mention = 
	| { type: "Owner", content?: Owner };

export interface File {
	file_id: FileId;
	uploaded_at: ApiDateTime;
	name: string;
	size: number;
	mime_type: string;
	system_wide: boolean;
}

export interface Activity {
	activity_stream_id: ActivityStreamId;
	activity_ordinal: number;
	occurred_at: ApiDateTime;
	user?: UserMin;
	payload: ActivityPayload;
	mentions: Mention[];
	files: File[];
}

export enum ActivityType {
	Comment = "Comment",
	QuestionCreated = "QuestionCreated",
	QuestionUpdated = "QuestionUpdated",
	DocumentUpdated = "DocumentUpdated",
	AssessedControlUpdated = "AssessedControlUpdated",
	RiskUpdated = "RiskUpdated",
	Review = "Review",
}

export interface ActivitySummary {
	counts: Record<ActivityType, number>;
}

export interface ActivityStream {
	activity_stream_id: ActivityStreamId;
	created_at: ApiDateTime;
	channel_name: string;
	summary: ActivitySummary;
}

export type Condition = 
	| { type: "Literal", content: boolean };

export interface AnswerPart<T> {
	enabled: Condition;
	config: T;
}

export interface YesNoAnswerConfig {
}

export interface AnswerOption {
	value: string;
	label: string;
	allow_other_text: boolean;
}

export interface SelectAnswerConfig {
	options: AnswerOption[];
	max_selected?: number;
}

export interface TextAnswerConfig {
	word_count_limit?: number;
}

export interface FilesAnswerConfig {
	min_number?: number;
	max_numer?: number;
}

export interface AnswerParts {
	yes_no: AnswerPart<YesNoAnswerConfig>;
	select: AnswerPart<SelectAnswerConfig>;
	text: AnswerPart<TextAnswerConfig>;
	files: AnswerPart<FilesAnswerConfig>;
}

export type OwnerPayload = 
	| { type: "Team", content: TeamMin }
	| { type: "User", content: UserMin };

export interface Owner {
	owner_id: OwnerId;
	owner_payload: OwnerPayload;
}

export interface ScopeAxisMin {
	axis_id: ScopeAxisId;
	name: string;
	color: string;
}

export interface ScopeMin {
	scope_id: ScopeId;
	name: string;
	owner?: Owner;
	axis: ScopeAxisMin;
}

export enum AccessLevel {
	Read = "Read",
	Write = "Write",
}

export interface CoreResponse {
	core_response_id: CoreResponseId;
	created_at: ApiDateTime;
	deleted?: Actioned;
	question_text: string;
	response_text: string;
	limited_to_scopes?: ScopeMin[];
	access_level: AccessLevel;
}

export interface RelevantCoreResponse {
	core_response: CoreResponse;
	question_cosine_distance: number;
}

export interface QuestionFactSummary {
	num_used_facts: number;
}

export interface Question {
	question_id: QuestionId;
	question_number?: string;
	text: string;
	guidance?: string;
	parts: AnswerParts;
	response_layers: BTreeMap<LayerType, ResponseLayer>;
	disable_analysis: boolean;
	prompt_modifiers: PromptModifiers;
	used_prompt_modifiers?: PromptModifiers;
	answer_is_original?: boolean;
	is_internal: boolean;
	deleted?: Actioned;
	relevant_core_responses: RelevantCoreResponse[];
	fact_summary: QuestionFactSummary;
	override_limited_to_scopes: boolean;
	limited_to_scopes?: ScopeMin[];
}

export interface QuestionSourceConnector {
	connector_id: string;
	name: string;
	logo_url: string;
}

export interface QuestionSourceMin {
	question_source_id: QuestionSourceId;
	name: string;
	connector: QuestionSourceConnector;
}

export interface ScopeIdList {
	scope_ids: ScopeId[];
}

export interface ScopeMatrix {
	rows: ScopeIdList[];
}

export interface AdHocQuestion {
	question: Question;
	created_at: ApiDateTime;
	asker?: UserMin;
	question_source?: QuestionSourceMin;
	source_url?: string;
	scope_matrix: ScopeMatrix;
	access_level: AccessLevel;
}

/**
 * Minimal version of an ad hoc question that does not contain the question,
 * only the additional information associated with ad hoc questions.
 */
export interface AdHocQuestionInfo {
	asker?: UserMin;
	question_source?: QuestionSourceMin;
	source_url?: string;
	scope_matrix: ScopeMatrix;
	access_level: AccessLevel;
}

export interface AdHocQuestionTask {
	adhoc_question: AdHocQuestion;
}

export enum AiReview {
	Positive = "Positive",
	Negative = "Negative",
	Neutral = "Neutral",
}

export interface AnswerReviewPart {
	review: AiReview;
	comment: string;
}

export interface AnswerReviewParts {
	relevance: AnswerReviewPart;
	accuracy: AnswerReviewPart;
	clarity: AnswerReviewPart;
	commercial: AnswerReviewPart;
}

export interface Apikey {
	apikey_id: ApikeyId;
	name: string;
	account_id: AccountId;
	apikey: string;
	created_at: ApiDateTime;
}

export enum ReviewStatus {
	Pending = "Pending",
	Commented = "Commented",
	RequestedChanges = "RequestedChanges",
	Approved = "Approved",
	Unrequested = "Unrequested",
	Rejected = "Rejected",
}

export interface ReviewAction {
	review_action_id: ReviewActionId;
	review_status: ReviewStatus;
	actioned: Actioned;
	comment?: string;
	reviewer: Owner;
}

export interface Reviewable {
	reviewable_id: ReviewableId;
	overall_status: ReviewStatus;
	reviews: Record<OwnerId, ReviewAction>;
}

export interface AssessedControl {
	control_id: ControlId;
	assessment_id: AssessmentId;
	activity_stream: ActivityStream;
	reviews?: Reviewable;
	scope_selection: ControlScopeSelectionAssessmentNode;
	scopes: Array<ControlScopeAssessmentAssessmentNode>;
	assess: ControlAssessmentAssessmentNode;
	relevant_risks: Array<RiskRelevantControlsAssessmentNode>;
}

export interface AssessedControlIdPair {
	assessment_id: AssessmentId;
	control_id: ControlId;
}

export interface ThirdPartyTierMin {
	tier_id: ThirdPartyTierId;
	title: string;
	color: string;
	ordinal: number;
}

export interface ThirdPartyTagMin {
	tag_id: ThirdPartyTagId;
	title: string;
	color: string;
}

export interface ThirdPartyMin {
	third_party_id: ThirdPartyId;
	name: string;
	external_id?: string;
	tier?: ThirdPartyTierMin;
	tags: ThirdPartyTagMin[];
	owner?: Owner;
	created_at: ApiDateTime;
	deleted?: Actioned;
	url: string;
	due_date?: ApiDateTime;
	score: number;
	num_assessments: number;
}

export interface DataRoom {
	data_room_id: DataRoomId;
	third_party: ThirdPartyMin;
	created_at: ApiDateTime;
	deleted?: Actioned;
	name: string;
}

export interface FrameworkMin {
	framework_id: FrameworkId;
	name: string;
	is_custom: boolean;
	is_enabled: boolean;
	icon_file?: File;
	owner?: Owner;
}

export type RiskQuantificationModel = 
	| { type: "Basic", content?: undefined }
	| { type: "ComponentWise", content: ComponentWiseRiskQuantificationModel };

export interface FrameworkVersionMin {
	framework_version_id: FrameworkVersionId;
	framework: FrameworkMin;
	created_at: ApiDateTime;
	version: string;
	specification: string;
	published_at?: ApiDateTime;
	risk_quantification: RiskQuantificationModel;
}

export interface TprmAsset {
	asset_id: TprmAssetId;
	name: string;
	description: string;
}

export interface OperationalContextMin {
	operational_context_id: OperationalContextId;
	third_party_id: ThirdPartyId;
	name: string;
	description: string;
	assets: TprmAsset[];
}

export interface RiskSummaryItem {
	accepted: boolean;
	level?: number;
	num_risks: number;
}

export interface AssessmentSummary {
	risks: RiskSummaryItem[];
	num_controls: number;
	num_met_controls: number;
}

export enum AssessmentStatus {
	Automating = "Automating",
	Review = "Review",
	Complete = "Complete",
	Archived = "Archived",
	Cancelled = "Cancelled",
}

export interface PendingBackgroundTask {
	background_task_id: BackgroundTaskId;
	name: string;
	created_at: ApiDateTime;
	scheduled_for: ApiDateTime;
	progress: number;
	channel_name: string;
}

export interface Assessment {
	assessment_id: AssessmentId;
	data_room: DataRoom;
	framework_version: FrameworkVersionMin;
	operational_context?: OperationalContextMin;
	created_at: ApiDateTime;
	completed_at?: ApiDateTime;
	summary: AssessmentSummary;
	owner?: Owner;
	status: AssessmentStatus;
	step: number;
	external_id?: string;
	/**
	 * If this is `None`, then the initial assessment or a reassessment is in
	 * progress.
	 */
	ai_automation_finished_at?: ApiDateTime;
	has_completed_initial_assessment: boolean;
	assessment_task?: PendingBackgroundTask;
	scheduled_to_run: boolean;
}

export interface AssessmentMin {
	assessment_id: AssessmentId;
	data_room: DataRoom;
}

/** This is the logical identifier for assessment nodes. */
export type AssessmentDecisionNode = 
	| { type: "SourceData", content?: undefined }
	| { type: "ControlScopeSelection", content: ControlNode }
	| { type: "ControlScopeAssessment", content: ControlScopeNode }
	| { type: "ControlAssessment", content: ControlNode }
	| { type: "IdentifiedRisks", content?: undefined }
	| { type: "IdentifiedRisk", content: RiskNode }
	| { type: "InherentRisk", content: RiskNode }
	| { type: "RiskRelevantControls", content: RiskNode }
	| { type: "ResidualRisk", content: RiskNode }
	| { type: "RiskAcceptance", content: RiskNode };

export type InvalidationCause = 
	| { type: "New", content?: undefined }
	| { type: "SourcesChanged", content?: undefined }
	| { type: "RiskIdentified", content?: undefined }
	| { type: "RiskUpdated", content?: undefined }
	| { type: "ControlScopeSelectionChanged", content?: undefined }
	| { type: "InherentRiskChanged", content: InherentRiskChangedContent }
	| { type: "ControlRelevanceChanged", content: ControlRelevanceChangedContent }
	| { type: "ControlMetChanged", content: ControlMetChangedContent }
	| { type: "ControlScopeMetChanged", content: ControlScopeMetChangedContent }
	| { type: "ResidualRiskChanged", content: ResidualRiskChangedContent };

export interface AssessmentNode {
	node_id: AssessmentNodeId;
	node: AssessmentDecisionNode;
	latest_decision?: AssessmentDecision;
	suggested_change?: AssessmentDecision;
	invalidation?: InvalidationCause[];
}

export interface AssessmentNodeMin {
	node_id: AssessmentNodeId;
}

export interface ThirdPartyNotificationMin {
	third_party_id: ThirdPartyId;
	name: string;
}

export interface AssessmentNotificationMin {
	assessment_id: AssessmentId;
	third_party: ThirdPartyNotificationMin;
}

export interface AssessmentWithQuestionnaires {
	assessment_id: AssessmentId;
	data_room: DataRoom;
	framework_version: FrameworkVersionMin;
	operational_context?: OperationalContextMin;
	created_at: ApiDateTime;
	completed_at?: ApiDateTime;
	summary: AssessmentSummary;
	owner?: Owner;
	status: AssessmentStatus;
	step: number;
	external_id?: string;
	/**
	 * If this is `None`, then the initial assessment or a reassessment is in
	 * progress.
	 */
	ai_automation_finished_at?: ApiDateTime;
	has_completed_initial_assessment: boolean;
	assessment_task?: PendingBackgroundTask;
	scheduled_to_run: boolean;
	latest_created_at?: ApiDateTime;
	latest_updated_at?: ApiDateTime;
	latest_locked_at?: ApiDateTime;
}

export enum AssetOwner {
	TrustCenter = "TrustCenter",
}

export interface Asset {
	asset_id: AssetId;
	title: string;
	owner?: AssetOwner;
	file: File;
}

export interface AssetMin {
	asset_id: AssetId;
	title: string;
}

export interface ServiceUser {
	user_id: UserId;
	type_: UserTypeService;
	name: string;
	description: string;
	primary_email?: Never;
	avatar_url?: Never;
}

export interface AuthenticatedUserApikey {
	user_apikey_id: UserApikeyId;
	account: AccountMin;
	user: ServiceUser;
}

export interface BatchAssessmentNotificationMin {
	third_party: ThirdPartyNotificationMin;
	assessment_total: number;
}

export interface BatchControlNotificationMin {
	assessment: AssessmentNotificationMin;
	control_total: number;
}

export enum DocumentCategory {
	Policy = "Policy",
	Certification = "Certification",
	Report = "Report",
	Other = "Other",
}

export interface BatchDocumentNotificationMin {
	document_category: DocumentCategory;
	document_total: number;
}

export interface QuestionnaireNotificationMin {
	questionnaire_id: QuestionnaireId;
	name: string;
	archived: boolean;
}

export interface BatchQuestionNotificationMin {
	questionnaire?: QuestionnaireNotificationMin;
	question_total: number;
}

export interface BatchQuestionnaireNotificationMin {
	archived: boolean;
	questionnaire_total: number;
}

export interface BatchRiskNotificationMin {
	risk_total: number;
	assessment: AssessmentNotificationMin;
}

export interface BatchThirdPartyQuestionnaireNotificationMin {
	third_party: ThirdPartyNotificationMin;
	questionnaire_count: number;
}

export interface BulkQuestionOverrideScopes {
	enable_override: boolean;
	limited_to_scopes?: ScopeId[];
}

export enum LayerType {
	External = "External",
	Internal = "Internal",
	AI = "AI",
}

export type BulkQuestionAction = 
	| { type: "Assign", content?: OwnerId }
	| { type: "SetStatus", content: QuestionStatus }
	| { type: "SetDueDate", content?: ApiDateTime }
	| { type: "SetText", content: string }
	| { type: "OverrideScopes", content: BulkQuestionOverrideScopes }
	| { type: "Delete", content?: undefined };

export interface BulkQuestionUpdateItem {
	question_id: QuestionId;
	action: BulkQuestionAction;
}

export interface BulkQuestionUpdate {
	layer_type: LayerType;
	items: BulkQuestionUpdateItem[];
}

export type BulkRiskAction = 
	| { type: "Assign", content?: OwnerId }
	| { type: "Delete", content?: undefined };

export interface BulkRiskUpdateItem {
	assessment_id: AssessmentId;
	risk_id: RiskId;
	action: BulkRiskAction;
}

export interface BulkRiskUpdate {
	items: BulkRiskUpdateItem[];
}

export enum BulkSourceAction {
	Delete = "Delete",
	RiskAssessInclude = "RiskAssessInclude",
	RiskAssessExclude = "RiskAssessExclude",
	ControlEvalInclude = "ControlEvalInclude",
	ControlEvalExclude = "ControlEvalExclude",
}

export interface BulkSourceUpdateItem {
	source_id: SourceId;
	action: BulkSourceAction;
}

export interface BulkSourceUpdate {
	items: BulkSourceUpdateItem[];
}

export interface CharacterConfig {
	lowercase: boolean;
	uppercase: boolean;
	number: boolean;
	symbol: boolean;
}

export enum TprmScopeType {
	Global = "Global",
	InternalApplication = "InternalApplication",
	ExternalApplication = "ExternalApplication",
	DataFlow = "DataFlow",
}

export interface ControlMin {
	control_id: ControlId;
}

export interface ControlGroup {
	control_group_id: ControlGroupId;
	title: string;
	description: string;
	num_controls: number;
	controls: ControlMin[];
}

export enum RiskAreaExampleRiskKind {
	Valid = "Valid",
	Invalid = "Invalid",
}

export interface RiskAreaExampleRisk {
	risk_area_example_risk_id: RiskAreaExampleRiskId;
	kind: RiskAreaExampleRiskKind;
	risk_text: string;
	reason: string;
}

export interface RiskArea {
	risk_area_id: RiskAreaId;
	title: string;
	description: string;
	examples: RiskAreaExampleRisk[];
}

export interface FrameworkProfile {
	framework_profile_id: FrameworkProfileId;
	framework_version: FrameworkVersionMin;
	created_at: ApiDateTime;
	name: string;
	scope_types: TprmScopeType[];
	control_groups: ControlGroup[];
	risk_areas: RiskArea[];
	custom_prompt: string;
}

export interface Risk {
	risk_id: RiskId;
	owner?: Owner;
	activity_stream: ActivityStream;
	assessment: AssessmentMin;
	risk_number: string;
	reviews?: Reviewable;
	identified: IdentifiedRiskAssessmentNode;
	inherent_risk: InherentRiskAssessmentNode;
	relevant_controls: RiskRelevantControlsAssessmentNode;
	residual_risk: ResidualRiskAssessmentNode;
	accepted: RiskAcceptanceAssessmentNode;
}

/**
 * All the information required to generate a chart. This is sent to the
 * report renderer service.
 */
export interface ChartContentPayload {
	assessment: Assessment;
	framework_profiles: FrameworkProfile[];
	risks: Risk[];
	assessed_controls: Record<ControlId, AssessedControl>;
}

export interface FactTextMin {
	fact_text_id: FactTextId;
	created_at: ApiDateTime;
	content: string;
}

export interface ClusteredFact {
	fact: FactTextMin;
	centroid_distance: number;
}

export type RichTextElement = 
	| { type: "Text", content: TextElement }
	| { type: "Mention", content: MentionElement };

export interface RichText {
	elements: RichTextElement[];
}

export interface CommentPayload {
	message: RichText;
}

export type BackgroundTaskResult = 
	/**
	 * Used for tasks that don't return a result
	 * 
	 * (e.g. they might update existing resources)
	 */
	| { type: "Empty", content?: undefined }
	| { type: "ExportAssessment", content: ExportedAssessment };

export interface CompleteBackgroundTask {
	background_task_id?: BackgroundTaskId;
	completed_at?: ApiDateTime;
	result: BackgroundTaskResult;
}

export enum ColorPalette {
	Gray = "gray",
	Red = "red",
	Pink = "pink",
	Purple = "purple",
	Cyan = "cyan",
	Blue = "blue",
	Teal = "teal",
	Green = "green",
	Yellow = "yellow",
	Orange = "orange",
}

export interface RiskThreshold {
	value: number;
	label: string;
	color: ColorPalette;
}

export interface RiskQuantificationModelComponent {
	label: string;
	description?: string;
	units?: string;
	thresholds?: RiskThreshold[];
	show_choice: boolean;
	show_numeric: boolean;
	ai_prompt: string;
	min_value?: number;
	max_value: number;
	step: number;
	bias?: number;
	weight: number;
}

export enum RiskQuantificationCombiningFunction {
	Add = "Add",
	Multiply = "Multiply",
	Min = "Min",
	Max = "Max",
}

export interface ComponentWiseRiskQuantificationModel {
	components: RiskQuantificationModelComponent[];
	combining_function?: RiskQuantificationCombiningFunction;
	units?: string;
	thresholds: RiskThreshold[];
	show_choice: boolean;
	show_numeric: boolean;
}

export interface ComputedRiskEvaluation {
	level: number;
	label: string;
	color: ColorPalette;
	value: number;
	components?: number[];
	reason: string;
}

export interface ConfluenceSite {
	id: string;
	name: string;
	url: string;
	avatar_url: string;
}

export interface ConfluenceSpace {
	id: string;
	name: string;
}

export interface ConfluenceDocumentSourceConfig {
	site: ConfluenceSite;
	space?: ConfluenceSpace;
	labels: ConfluenceDocumentSourceLabels;
}

export interface ConfluenceLabel {
	id: string;
	name: string;
	prefix: string;
}

export interface ConsentArgs {
	external_redirect: string;
}

export interface ConsentResponse {
	client_account: AccountMin;
	scope: AccessScope;
}

export interface ControlGroupMin {
	control_group_id: ControlGroupId;
	title: string;
	description: string;
	num_controls: number;
}

export interface FrameworkProfileMin {
	framework_profile_id: FrameworkProfileId;
	framework_version: FrameworkVersionMin;
	created_at: ApiDateTime;
	name: string;
	scope_types: TprmScopeType[];
}

export interface Control {
	control_id: ControlId;
	group: ControlGroupMin;
	profile: FrameworkProfileMin;
	control_number?: string;
	title: string;
	description: string;
	is_required: boolean;
}

export interface ControlAssessedPayload {
	met?: boolean;
	explanation: string;
}

export interface ControlAssessmentContent {
	met?: boolean;
	explanation: string;
	evidence_ids?: BTreeSet<ControlEvidenceId>;
}

export type SourceMinKind = 
	| { type: "Document", content: DocumentSourceMinKind }
	| { type: "Questionnaire", content: QuestionnaireSourceMinKind };

export interface TprmScopeMin {
	scope_id: TprmScopeId;
	data_room: DataRoom;
	deleted?: Actioned;
	name: string;
	scope_type: TprmScopeType;
}

export interface SourceMin {
	source_id: SourceId;
	source_number: string;
	name: string;
	kind: SourceMinKind;
	created_at: ApiDateTime;
	analyzed_at?: ApiDateTime;
	deleted?: Actioned;
	scope: TprmScopeMin;
	risk_visible: boolean;
	control_visible: boolean;
}

export interface ControlEvidence {
	evidence_id: ControlEvidenceId;
	source: SourceMin;
	source_context: string;
	deleted?: Actioned;
}

export interface ControlMetChangedContent {
	control: ControlMin;
	old_met?: boolean;
}

export interface ControlNode {
	control_id: ControlId;
}

export interface ControlNotificationMin {
	control_id: ControlId;
	title: string;
	group: ControlGroupMin;
	assessment: AssessmentNotificationMin;
}

export interface ControlRelevanceChangedContent {
	control: ControlMin;
	was_relevant: boolean;
}

export interface ControlScopeAssessmentContent {
	met?: boolean;
	explanation: string;
	evidence_ids: BTreeSet<ControlEvidenceId>;
}

export interface ControlScopeMetChangedContent {
	scope_id: TprmScopeId;
	old_met?: boolean;
}

export interface ControlScopeNode {
	control_id: ControlId;
	scope_id: TprmScopeId;
}

export interface ControlScopeSelectionContent {
	scope_ids: BTreeSet<TprmScopeId>;
}

export interface CountResolutionsArgs {
	assigned_to_me?: boolean;
}

export interface Counterparty {
	counterparty_id: CounterpartyId;
	name: string;
	url: string;
	crm_connection_id?: CrmConnectionId;
	crm_last_modified?: ApiDateTime;
	uri?: string;
	language_code: string;
}

export interface CreateAccessToken {
	user_id: UserId;
	client_account_id: AccountId;
	affiliated_account_id?: AccountId;
	scope: AccessScope;
}

export type CreateMention = 
	| { type: "Owner", content: OwnerId };

export interface CreateActivity {
	user_id?: UserId;
	payload: ActivityPayload;
	mentions: CreateMention[];
	file_ids: FileId[];
}

export interface ResponseOption {
	value: string;
	other_text?: string;
}

export enum QuestionStatus {
	Automating = "Automating",
	Respond = "Respond",
	Review = "Review",
	Complete = "Complete",
}

export interface CreateResponseLayer {
	response_yes_no?: boolean;
	response_select?: ResponseOption[];
	response_text?: string;
	status: QuestionStatus;
	owner_id?: OwnerId;
	due_date?: ApiDateTime;
	responder_id?: UserId;
}

export interface CreateQuestionGroup {
	name: string;
}

export interface CreateQuestion {
	question_number?: string;
	text: string;
	guidance?: string;
	parts?: AnswerParts;
	response_layer: CreateResponseLayer;
	question_group?: CreateQuestionGroup;
}

export interface CreateAdHocQuestion {
	question: CreateQuestion;
	asker_id?: UserId;
	question_source_id?: QuestionSourceId;
	source_url?: string;
	scope_matrix: ScopeMatrix;
}

export interface CreateAssessment {
	data_room_id: DataRoomId;
	framework_version_id: FrameworkVersionId;
	operational_context_id?: OperationalContextId;
	owner_id?: OwnerId;
	external_id?: string;
}

export type AssessmentDecisionContent = 
	| { type: "SourceData", content?: undefined }
	| { type: "ControlScopeSelection", content: ControlScopeSelectionContent }
	| { type: "ControlScopeAssessment", content: ControlScopeAssessmentContent }
	| { type: "ControlAssessment", content: ControlAssessmentContent }
	| { type: "IdentifiedRisks", content?: undefined }
	| { type: "IdentifiedRisk", content: IdentifiedRiskContent }
	| { type: "InherentRisk", content: RiskEvaluationContent }
	| { type: "RiskRelevantControls", content: RiskRelevantControlsContent }
	| { type: "ResidualRisk", content: RiskEvaluationContent }
	| { type: "RiskAcceptance", content: RiskAcceptanceContent };

export interface RecordAssessmentDecision {
	was_suggested: boolean;
	content: AssessmentDecisionContent;
}

export interface CreateAssessmentDecision {
	node_id: AssessmentNodeId;
	record: RecordAssessmentDecision;
}

export interface CreateAsset {
	title: string;
	file_id: FileId;
	owner?: AssetOwner;
}

export enum CacheDirective {
	/** Cache the response */
	Cache = "Cache",
	/** Do not retrive from cache or cache the response */
	BypassCache = "BypassCache",
}

export interface CreateAutoComplete {
	cache_directive: CacheDirective;
}

export interface CreateComment {
	message: RichText;
	mentions: CreateMention[];
	file_ids: FileId[];
}

export interface CreateControl {
	control_number?: string;
	title: string;
	description: string;
	is_required?: boolean;
}

export interface CreateControlGroup {
	title: string;
	description?: string;
}

export interface CreateCoreResponse {
	question_text: string;
	response_text: string;
	limited_to_scope_ids?: ScopeId[];
}

export interface CreateCounterparty {
	name: string;
	url: string;
	language_code: string;
}

export type CrmConnectionConfig = 
	| { type: "Hubspot", content: HubspotCrmConnectionConfig };

export interface CreateCrmConnection {
	name: string;
	connector_id: string;
	external_authorization_id?: ExternalAuthorizationId;
	config: CrmConnectionConfig;
}

export interface CreateDataRoom {
	name: string;
}

export type ReviewPeriod = 
	| { type: "Days", content: number }
	| { type: "None", content?: undefined };

export enum SharingClassification {
	Internal = "Internal",
	NDA = "NDA",
	External = "External",
}

export enum DocumentAuthority {
	File = "File",
	Markdown = "Markdown",
	FileAndMarkdown = "FileAndMarkdown",
	Template = "Template",
}

export type Config = 
	| { type: "Password", content: PasswordPolicyConfig }
	| { type: "None", content?: undefined };

export enum CertificationType {
	Crest = "Crest",
	CsaStar = "CsaStar",
	CyberEssentials = "CyberEssentials",
	Iso27001 = "Iso27001",
	Iso27017 = "Iso27017",
	Iso27018 = "Iso27018",
	Iso27701 = "Iso27701",
	Soc2 = "Soc2",
	Soc3 = "Soc3",
}

export interface CreateDocument {
	name: string;
	category: DocumentCategory;
	review_period: ReviewPeriod;
	owner_id: OwnerId;
	sharing_classification: SharingClassification;
	authority: DocumentAuthority;
	file_id?: FileId;
	markdown?: string;
	authoring_config?: Config;
	authoring_template?: string;
	limited_to_scope_ids?: ScopeId[];
	certification_type?: CertificationType;
	certification_date_granted?: ApiDateTime;
	certification_date_expires?: ApiDateTime;
	certification_auditor_url?: string;
	certification_audit_report?: DocumentId;
}

export type DocumentSourceConfig = 
	| { type: "Platformed", content?: undefined }
	| { type: "GDrive", content: GDriveDocumentSourceConfig }
	| { type: "Microsoft", content: MicrosoftDocumentSourceConfig }
	| { type: "Confluence", content: ConfluenceDocumentSourceConfig }
	| { type: "Notion", content: NotionDocumentSourceConfig }
	| { type: "IManage", content: IManageDocumentSourceConfig }
	| { type: "Web", content: WebDocumentSourceConfig };

export interface CreateDocumentSource {
	name: string;
	connector_id: string;
	external_authorization_id?: ExternalAuthorizationId;
	config: DocumentSourceConfig;
	default_owner_id: OwnerId;
	default_sharing_classification: SharingClassification;
	default_limited_to_scope_ids?: ScopeId[];
}

export interface CreateDocumentSourceKind {
	file_id: FileId;
	context: string;
}

export enum EsignatureProviderConnectorType {
	Docusign = "docusign",
}

export enum ExternalAccountReferenceType {
	VtQuestionSourceSlack = "VtQuestionSourceSlack",
	MailingListPostmark = "MailingListPostmark",
	VtEsignatureProviderDocusign = "VtEsignatureProviderDocusign",
}

export interface PopulateExternalAccountReference {
	reference_type: ExternalAccountReferenceType;
	reference: string;
}

export type EsignatureProviderConfig = 
	| { type: "Docusign", content: DocusignEsignatureProviderConfig };

export interface CreateEsignatureProvider {
	name: string;
	connector_type: EsignatureProviderConnectorType;
	external_authorization_id?: ExternalAuthorizationId;
	external_reference?: PopulateExternalAccountReference;
	config: EsignatureProviderConfig;
}

export enum ExternalAuthorizationProvider {
	Google = "Google",
	Atlassian = "Atlassian",
	Hubspot = "Hubspot",
	Slack = "Slack",
	Microsoft = "Microsoft",
	Docusign = "Docusign",
	Notion = "Notion",
	IManage = "imanage",
	Manual = "Manual",
}

export type CreateExternalAuthorizationPayload = 
	| { type: "Oauth", content: CreateExternalAuthorizationOAuth }
	| { type: "Raw", content: CreateExternalAuthorizationRaw };

export interface CreateExternalAuthorization {
	provider: ExternalAuthorizationProvider;
	purpose: string;
	payload: CreateExternalAuthorizationPayload;
}

export interface CreateExternalAuthorizationOAuth {
	code: string;
}

export interface CreateExternalAuthorizationRaw {
	value: string;
}

export enum ExternalNonceProvider {
	IManage = "imanage",
}

/**
 * Contains metadata only available on the initial call to authorize an OAuth token, such as an external
 * user ID or a workspace identifier.
 */
export interface CreatedAuthorizationMetadata {
	external_account_id?: string;
	external_user_id?: string;
	external_domain?: string;
}

export interface CreateExternalNonce {
	provider: ExternalNonceProvider;
	purpose: string;
	metadata: CreatedAuthorizationMetadata;
}

export type FeedEventPayload = 
	| { type: "TprmAssessmentStatusChanged", content: TprmAssessmentStatusChangedEventPayload }
	| { type: "TprmAssessmentExportCompleted", content: TprmAssessmentExportCompletedEventPayload };

export interface CreateFeedEvent {
	account_id: AccountId;
	payload: FeedEventPayload;
	user_id?: UserId;
}

export interface CreateFramework {
	name: string;
	is_custom: boolean;
	icon_file_id?: FileId;
}

export interface CreateFrameworkProfile {
	name: string;
	scope_types: TprmScopeType[];
}

export interface CreatePredefinedRisk {
	title: string;
	description: string;
	contextual_guidance?: string;
	always_include?: boolean;
}

export interface PredefinedRisk {
	risk: CreatePredefinedRisk;
}

export interface CreateFrameworkVersion {
	version: string;
	specification: string;
	risk_quantification: RiskQuantificationModel;
	predefined_risks?: PredefinedRisk[];
}

export interface CreateLibrarySection {
	name: string;
	description: string;
	parent_scope_id?: ScopeId;
	parent_section_id?: LibrarySectionId;
}

export type LoginMethodInner = 
	| { type: "UsernamePassword", content: EmptyLoginMethodPayload }
	| { type: "GoogleOAuth", content: EmptyLoginMethodPayload }
	| { type: "MicrosoftOAuth", content: EmptyLoginMethodPayload }
	| { type: "GenericOauth", content: LoginMethodGenericOauth }
	| { type: "GenericSaml", content: LoginMethodGenericSaml };

export interface CreateLoginMethod {
	name: string;
	inner: LoginMethodInner;
}

export enum NotificationSinkConnectorType {
	Slack = "slack",
}

export interface NotificationSinkConnector {
	connector_type: NotificationSinkConnectorType;
	name: string;
	logo_url: string;
}

export type NotificationSinkConfig = 
	| { type: "Slack", content: SlackNotificationSinkConfig };

export interface CreateNotificationSink {
	name: string;
	connector: NotificationSinkConnector;
	external_authorization_id?: ExternalAuthorizationId;
	config: NotificationSinkConfig;
}

export interface CreateOperationalContext {
	third_party_id: ThirdPartyId;
	name: string;
	description: string;
	assets: TprmAssetId[];
}

export interface CreateOrUpdateShowcaseUpdateMessage {
	title?: string;
	content?: string;
}

export type CreateDocumentationAttachment = 
	| { type: "Document", content: {
	document_id: DocumentId;
}}
	| { type: "StandaloneFile", content: {
	standalone_file_id: StandaloneFileId;
}};

export interface CreateQuestionDocumentation {
	attachment: CreateDocumentationAttachment;
}

export interface CreateQuestionInSection {
	section_id: SectionId;
	question_group_id?: QuestionGroupId;
	question: CreateQuestion;
}

export type QuestionSourceConfig = 
	| { type: "Slack", content: SlackQuestionSourceConfig };

export interface CreateQuestionSource {
	name: string;
	connector_id: string;
	external_authorization_id: ExternalAuthorizationId;
	config: QuestionSourceConfig;
}

export interface CreateSection {
	title: string;
	description: string;
	questions: CreateQuestion[];
}

export interface CreateQuestionnaire {
	counterparty_id: CounterpartyId;
	name: string;
	owner_id?: OwnerId;
	sections?: CreateSection[];
	due_date?: ApiDateTime;
	archived?: boolean;
	scope_matrix?: ScopeMatrix;
	is_internal?: boolean;
	prompt_modifiers?: PromptModifiers;
}

export interface CreateQuestionnaireDocumentation {
	attachment: CreateDocumentationAttachment;
	linked_to_questionnaire: boolean;
	linked_question_ids: QuestionId[];
}

export interface CreateQuestionnaireSourceKind {
	questionnaire_id: TprmQuestionnaireId;
}

export interface CreateRegisteredUser {
	name: string;
	email: string;
	external_ids?: Record<LoginMethodId, string>;
}

export interface CreateReviewAction {
	comment?: string;
	review_status: ReviewStatus;
	reviewer_id: OwnerId;
}

export interface CreateRisk {
	title: string;
	description: string;
	is_predefined_risk: boolean;
}

export interface CreateRiskAreaExampleRisk {
	kind: RiskAreaExampleRiskKind;
	risk_text: string;
	reason?: string;
}

export interface CreateRiskArea {
	title: string;
	description?: string;
	examples?: CreateRiskAreaExampleRisk[];
}

export interface CreateRiskContext {
	risk_area_id?: RiskAreaId;
	source_id: SourceId;
	source_context: string;
}

export interface CreateScimConfig {
	name: string;
	send_invitations: boolean;
}

export interface CreateScope {
	name: string;
	axis_id: ScopeAxisId;
}

export interface CreateScopeAxis {
	name: string;
	description?: string;
	color: string;
}

export interface CreateServiceUser {
	name: string;
	description: string;
}

export type CreateSourceKind = 
	| { type: "Document", content: CreateDocumentSourceKind }
	| { type: "Questionnaire", content: CreateQuestionnaireSourceKind };

export interface CreateSource {
	name: string;
	/** Which external questionnaire created this source? */
	external_questionnaire_id?: TprmExternalQuestionnaireId;
	kind: CreateSourceKind;
}

export interface CreateStandaloneFile {
	file_id: FileId;
	sharing_classification: SharingClassification;
}

export interface CreateTeam {
	name: string;
	description?: string;
}

export interface CreateThirdParty {
	name: string;
	external_id?: string;
	url: string;
	tier_id?: ThirdPartyTierId;
	tag_ids?: ThirdPartyTagId[];
	due_date?: ApiDateTime;
	owner_id?: OwnerId;
}

export interface CreateThirdPartyTag {
	title: string;
	color: string;
}

export interface CreateThirdPartyTier {
	title: string;
	color: string;
}

export interface CreateTprmAsset {
	name: string;
	description: string;
}

export interface CreateTprmExternalQuestionnaire {
	data_room_id: DataRoomId;
	questionnaire_template_id: TprmQuestionnaireTemplateId;
	parent_external_questionnaire_id?: TprmExternalQuestionnaireId;
	use_latest_version: boolean;
}

/** This is serialized as JSON to the database, so be careful with changes. */
export type TprmAnswerConfig = 
	| { type: "Text", content: TprmTextAnswerConfig }
	| { type: "Files", content: TprmFilesAnswerConfig }
	| { type: "Select", content: TprmSelectAnswerConfig }
	| { type: "SubQuestionnaires", content: TprmSubQuestionnairesAnswerConfig };

/** This is serialized as JSON to the database, so be careful with changes. */
export type TprmCondition = 
	| { type: "Literal", content: boolean }
	| { type: "SelectMatches", content: {
	question_template_key: string;
	criteria: TprmConditionSelectMatchesCriteria;
	values: string[];
}}
	| { type: "Count", content: {
	question_template_key: string;
	operator: TprmConditionComparisonOperator;
	value: number;
}}
	| { type: "And", content: TprmCondition[] }
	| { type: "Or", content: TprmCondition[] }
	| { type: "Not", content: TprmCondition };

export interface CreateTprmQuestionTemplate {
	/**
	 * The key is a stable identifier that is used to reference the question
	 * from conditions.
	 */
	key?: string;
	text: string;
	guidance?: string;
	is_required: boolean;
	answer_config: TprmAnswerConfig;
	condition?: TprmCondition;
}

export interface CreateTprmQuestionnaireTemplate {
	name: string;
	parent_questionnaire_template_id?: TprmQuestionnaireTemplateId;
}

export enum TprmReportTemplateType {
	Management = "Management",
	ItAssessment = "ItAssessment",
	Audit = "Audit",
}

export interface CreateTprmReportTemplate {
	name: string;
	type_: TprmReportTemplateType;
	ai_summary_prompts?: string[];
}

export interface CreateTprmScope {
	name: string;
	scope_type: TprmScopeType;
	external_questionnaire_id?: TprmExternalQuestionnaireId;
}

export interface CreateTprmSectionTemplate {
	title: string;
	description?: string;
	questions: CreateTprmQuestionTemplate[];
}

export interface CreateTrustCenterContent {
	title: string;
	content: string;
}

export interface CreateVerifiedDomain {
	domain: string;
	enable_self_enrollment: boolean;
}

export type ExternalAuthorizationPayload = 
	| { type: "Oauth", content: ExternalAuthorizationOauth }
	| { type: "Raw", content: ExternalAuthorizationRaw };

export interface CreatedExternalAuthorization {
	external_authorization_id: ExternalAuthorizationId;
	user?: UserMin;
	provider: ExternalAuthorizationProvider;
	metadata: CreatedAuthorizationMetadata;
	purpose: string;
	payload: ExternalAuthorizationPayload;
	access_token_expiry?: ApiDateTime;
	refresh_at?: ApiDateTime;
}

export interface CreatedExternalNonce {
	nonce: string;
	provider: ExternalNonceProvider;
}

export interface CrmAccountInfo {
	account_id: string;
}

export interface CrmConnector {
	connector_id: string;
	name: string;
	logo_url: string;
}

export interface ExternalAuthorizationMin {
	external_authorization_id: ExternalAuthorizationId;
	user?: UserMin;
	provider: ExternalAuthorizationProvider;
	purpose: string;
	access_token_expiry?: ApiDateTime;
	refresh_at?: ApiDateTime;
	refresh_attempts: number;
}

export interface CrmConnection {
	crm_connection_id: CrmConnectionId;
	name: string;
	connector: CrmConnector;
	config: CrmConnectionConfig;
	external_authorization: ExternalAuthorizationMin;
	sync?: PendingBackgroundTask;
}

export interface CrmConnectionMin {
	crm_connection_id: CrmConnectionId;
	name: string;
	connector: CrmConnector;
	sync?: PendingBackgroundTask;
}

export interface CrmCounterparty {
	crm_object_id: string;
	domain: string;
	name: string;
	uri: string;
	last_modified: ApiDateTime;
	archived: boolean;
	language_code?: string;
}

export interface CrmDeal {
	name: string;
	amount?: number;
	stage_key?: string;
	crm_counterparty_id: string;
	close_date?: ApiDateTime;
	uri: string;
	last_modified: ApiDateTime;
}

export enum CustomDomainType {
	TrustCenter = "TrustCenter",
	Collect = "Collect",
}

export interface CustomDomain {
	domain: string;
	account_id: AccountId;
	domain_type: CustomDomainType;
	redirect_to: boolean;
}

export interface Deal {
	deal_id: DealId;
	counterparty: Counterparty;
	crm_connection_id: CrmConnectionId;
	name: string;
	amount?: number;
	owner?: Owner;
	created_at: ApiDateTime;
	close_date?: ApiDateTime;
	uri: string;
}

export interface DeleteDocumentSourceArgs {
	retain_documents?: boolean;
}

export enum TrustCenterAccess {
	Allowed = "Allowed",
	GeneralGrant = "GeneralGrant",
	SpecificGrant = "SpecificGrant",
	Denied = "Denied",
}

export interface DocumentMin {
	document_id: DocumentId;
	name: string;
	category: DocumentCategory;
	created_at: ApiDateTime;
	last_update: Actioned;
	last_review?: ApiDateTime;
	next_review?: ApiDateTime;
	review_period: ReviewPeriod;
	sharing_classification: SharingClassification;
	owner?: Owner;
	authority: DocumentAuthority;
	file: File;
	trust_center_visibility: TrustCenterAccess;
	trust_center_readability: TrustCenterAccess;
	trust_center_esignature_template_id?: EsignatureTemplateId;
	access_level: AccessLevel;
	is_deleting: boolean;
}

export interface FactGenPrompt {
	custom_text_prompt?: string;
}

export interface Document {
	document_id: DocumentId;
	name: string;
	category: DocumentCategory;
	created_at: ApiDateTime;
	last_update: Actioned;
	last_analyzed?: ApiDateTime;
	last_review?: ApiDateTime;
	next_review?: ApiDateTime;
	review_period: ReviewPeriod;
	sharing_classification: SharingClassification;
	owner?: Owner;
	authority: DocumentAuthority;
	file: File;
	markdown: string;
	markdown_modified: boolean;
	extract?: PendingBackgroundTask;
	authoring_template?: string;
	authoring_config?: Config;
	limited_to_scopes?: ScopeMin[];
	trust_center_visibility: TrustCenterAccess;
	trust_center_readability: TrustCenterAccess;
	trust_center_esignature_template_id?: EsignatureTemplateId;
	deleted?: Actioned;
	certification_type?: CertificationType;
	certification_date_granted?: ApiDateTime;
	certification_date_expires?: ApiDateTime;
	certification_auditor_url?: string;
	certification_audit_report?: DocumentMin;
	activity_stream: ActivityStream;
	access_level: AccessLevel;
	fact_gen_prompt: FactGenPrompt;
	document_source_id?: DocumentSourceId;
}

/** This is inserted into the database as JSONB so be careful with changes. */
export interface DocumentAccessRequest {
	document_ids: DocumentId[];
	email: string;
	name: string;
	phone: string;
	point_of_contact: string;
	message: string;
	already_signed_nda?: boolean;
}

export interface DocumentExternalMin {
	document_id: DocumentId;
	name: string;
	category: DocumentCategory;
	last_update: ApiDateTime;
	is_readable: boolean;
	requires_esignature: boolean;
}

export interface DocumentExternal {
	document_id: DocumentId;
	name: string;
	category: DocumentCategory;
	last_update: ApiDateTime;
	is_readable: boolean;
	requires_esignature: boolean;
	certification_type?: CertificationType;
	certification_date_granted?: ApiDateTime;
	certification_date_expires?: ApiDateTime;
	certification_auditor_url?: string;
	certification_audit_report?: DocumentExternalMin;
}

export interface DocumentNotificationMin {
	document_id: DocumentId;
	name: string;
	category: DocumentCategory;
}

export interface DocumentSourceConnector {
	connector_id: string;
	name: string;
	logo_url: string;
}

export interface ReportedErrorCatalogMin {
	catalog_id: ErrorCatalogId;
	created_at: ApiDateTime;
	num_errors: number;
}

export interface DocumentSource {
	document_source_id: DocumentSourceId;
	name: string;
	connector: DocumentSourceConnector;
	external_authorization?: ExternalAuthorizationMin;
	config: DocumentSourceConfig;
	num_documents: number;
	sync?: PendingBackgroundTask;
	last_sync_at?: ApiDateTime;
	last_error_catalog?: ReportedErrorCatalogMin;
	default_owner: Owner;
	default_sharing_classification: SharingClassification;
	default_limited_to_scopes?: ScopeMin[];
}

export interface DocumentSourceKind {
	file: File;
	markdown?: string;
	extract?: PendingBackgroundTask;
	context: string;
}

export interface DocumentSourceMin {
	document_source_id?: DocumentSourceId;
	name: string;
	connector: DocumentSourceConnector;
	num_documents: number;
	sync?: PendingBackgroundTask;
	last_sync_at?: ApiDateTime;
	last_error_catalog?: ReportedErrorCatalogMin;
}

export interface DocumentSourceMinKind {
	file: File;
}

export type DocumentationAttachment = 
	| { type: "Document", content: DocumentMin }
	| { type: "StandaloneFile", content: StandaloneFile };

export interface QuestionDocumentation {
	question_id: QuestionId;
	linked_at: ApiDateTime;
	linked_by?: UserMin;
}

export interface Documentation {
	documentation_id: DocumentationId;
	attachment: DocumentationAttachment;
	linked_to_questionnaire: boolean;
	linked_questions: QuestionDocumentation[];
	created_at: ApiDateTime;
}

export interface DocusignEsignatureProviderConfig {
	account_name: string;
	base_uri: string;
}

export interface DownloadOptions {
	override_name?: string;
}

export interface EmptyLoginMethodPayload {
}

export interface EnableTwoFaTotp {
	secret: string;
	code: string;
}

export interface ErrorResponse {
	error: string;
	reference?: Uuid;
	type_: string;
	sub_type?: string;
	extra?: IValue;
}

export interface EsignatureProviderConnector {
	connector_type: EsignatureProviderConnectorType;
	name: string;
	logo_url: string;
}

export interface ExternalAuthorization {
	external_authorization_id: ExternalAuthorizationId;
	user?: UserMin;
	provider: ExternalAuthorizationProvider;
	purpose: string;
	payload: ExternalAuthorizationPayload;
	access_token_expiry?: ApiDateTime;
	refresh_at?: ApiDateTime;
	refresh_attempts: number;
}

export interface EsignatureProvider {
	esignature_provider_id: EsignatureProviderId;
	account_id: AccountId;
	name: string;
	config: EsignatureProviderConfig;
	connector: EsignatureProviderConnector;
	external_authorization?: ExternalAuthorization;
	external_account_id?: string;
	last_sync_at?: ApiDateTime;
	external_reference_id?: ExternalAccountReferenceId;
}

export interface EsignatureProviderMin {
	esignature_provider_id: EsignatureProviderId;
	account_id: AccountId;
	name: string;
	config: EsignatureProviderConfig;
	connector: EsignatureProviderConnector;
	last_sync_at?: ApiDateTime;
}

export interface EsignatureTemplate {
	template_id: EsignatureTemplateId;
	external_template_id: string;
	name: string;
	provider: EsignatureProviderMin;
}

export interface ExceptionConfig {
	compromised_password_process: string;
	non_compliance: string;
}

export interface ExportedAssessment {
	file_id: FileId;
	assessment_id: AssessmentId;
}

export interface ExternalAuthorizationOauth {
	access_token: string;
}

export interface ExternalAuthorizationRaw {
	value: string;
}

export interface ExternalDocument {
	uri: string;
	last_modified: ApiDateTime;
}

export interface ExternalUser {
	user_id: UserId;
	type_: UserTypeExternal;
	name?: Never;
	primary_email?: string;
	avatar_url?: Never;
}

export interface RegisteredUserMin {
	user_id: UserId;
	type_?: UserTypeRegistered;
	name: string;
	primary_email: string;
	avatar_url?: string;
}

export interface ExternalWhoAmIResponse {
	user: RegisteredUserMin;
	client_account: AccountMin;
	affiliated_account?: AccountMin;
	internal_mode: boolean;
}

export interface LibrarySectionWithoutScope {
	library_section_id: LibrarySectionId;
	name: string;
	description?: string;
	owner?: Owner;
	parent_section_id?: LibrarySectionId;
	deleted?: Actioned;
}

export interface ScopedLibrarySection {
	scope?: ScopeMin;
	library_section?: LibrarySectionWithoutScope;
}

export interface FactSourceDocument {
	document_id: DocumentId;
	document_name: string;
	document_revision: ApiDateTime;
	document_category: DocumentCategory;
	limited_to_scopes?: ScopeMin[];
	explanation_chunk: string;
	deleted?: Actioned;
}

export type FactSourceQuestionParent = 
	| { type: "Section", content: {
	questionnaire_id: QuestionnaireId;
	questionnaire_name: string;
	questionnaire_archived_at?: ApiDateTime;
	section_title: string;
	questionnaire_date: ApiDateTime;
}}
	| { type: "AdHoc", content: {
	question_date: ApiDateTime;
}};

export interface FactSourceQuestion {
	parent: FactSourceQuestionParent;
	question_id: QuestionId;
	question_number?: string;
	question_text: string;
	response_parts: AnswerParts;
	response_yes_no?: boolean;
	response_select?: ResponseOption[];
	response_text?: string;
	limited_to_scopes?: ScopeMin[];
	deleted?: Actioned;
}

export interface FactSourceEntity {
	entity_id: EntityId;
	limited_to_scopes?: ScopeMin[];
	deleted?: Actioned;
}

export interface Sources {
	document_sources: FactSourceDocument[];
	question_sources: FactSourceQuestion[];
	subgraph_sources: FactSourceEntity[];
}

export enum ResolutionType {
	Contradiction = "Contradiction",
	Stale = "Stale",
	Recurrent = "Recurrent",
	Sensitive = "Sensitive",
}

export interface ResolutionMin {
	resolution_id: ResolutionId;
	scope?: ScopeMin;
	resolution_type: ResolutionType;
	num_involved_facts: number;
	reason: string;
	review_necessity: number;
	review_explanation: string;
	created_at: ApiDateTime;
	owner?: Owner;
	resolved?: Actioned;
	ignored?: Actioned;
	is_internal: boolean;
}

export interface Fact {
	fact_id: FactId;
	representative_text: FactTextMin;
	all_texts: FactTextMin[];
	library_section: ScopedLibrarySection;
	active_sources: Sources;
	deleted_sources: Sources;
	resolutions: ResolutionMin[];
	created_at: ApiDateTime;
	disabled?: Actioned;
	deleted?: Actioned;
}

export interface FactCluster {
	cluster_id: FactClusterId;
	title: string;
	children?: FactCluster[];
	equivalence_count: number;
}

export interface FactClusterMin {
	cluster_id: FactClusterId;
	title: string;
}

export interface FactMin {
	fact_id: FactId;
	representative_text: FactTextMin;
	library_section: ScopedLibrarySection;
	created_at: ApiDateTime;
	disabled?: Actioned;
	deleted?: Actioned;
}

export interface ScopedLibrarySectionId {
	scope_id?: ScopeId;
	library_section_id?: LibrarySectionId;
}

export interface LibrarySectionFilter {
	scoped_id: ScopedLibrarySectionId;
	recursive: boolean;
}

export interface PaginationArgs {
	offset?: number;
	limit?: number;
}

export interface FactSearchArgs {
	search?: string;
	section_filter?: LibrarySectionFilter;
	pagination?: PaginationArgs;
}

export interface FeedEvent {
	feed_event_id: FeedEventId;
	payload: FeedEventPayload;
	user_id?: UserId;
}

export interface FilterAssessments {
	framework_id?: FrameworkId;
	framework_version_id?: FrameworkVersionId;
	third_party_id?: ThirdPartyId;
	data_room_id?: DataRoomId;
	completed?: boolean;
}

export enum Environment {
	Development = "development",
	Test = "test",
	Staging = "staging",
	Production = "production",
}

export interface PublicCredentials {
	sentry_dsn: string;
	google_client_id: string;
	ms_client_id: string;
	atlassian_client_id: string;
	pusher_key: string;
	pusher_cluster: string;
	hubspot_client_id: string;
	slack_client_id: string;
	docusign_client_id: string;
	docusign_endpoint: string;
	notion_client_id: string;
	imanage_client_id: string;
	posthog_app_key: string;
	posthog_trust_center_key: string;
	posthog_host: string;
	logodev_pubkey: string;
}

export interface FrontendEnvironment {
	environment: Environment;
	release: Release;
	app_url: string;
	trust_url: string;
	collect_url: string;
	credentials: PublicCredentials;
	custom_domain?: CustomDomain;
}

export interface GDriveDocumentSourceConfig {
	folder_id: string;
	folder_name: string;
	folder_icon_url: string;
}

export interface TprmReportTemplate {
	report_template_id: TprmReportTemplateId;
	name: string;
	type_: TprmReportTemplateType;
	ai_summary_prompts: string[];
}

export interface RiskContext {
	risk_context_id: RiskContextId;
	risk_area?: RiskArea;
	source: SourceMin;
	source_context: string;
	deleted?: Actioned;
}

export interface ReportSummary {
	summary: string;
	last_updated: Actioned;
}

/**
 * All the information required to generate a report. This is sent to the
 * report renderer service.
 */
export interface ReportContentPayload {
	report_template: TprmReportTemplate;
	assessment: Assessment;
	control_evidences: Record<ControlEvidenceId, ControlEvidence>;
	risk_contexts: Record<RiskContextId, RiskContext>;
	scopes: Record<TprmScopeId, TprmScopeMin>;
	framework_profiles: FrameworkProfile[];
	activity_streams?: Record<ActivityStreamId, Activity[]>;
	controls: Record<ControlId, Control>;
	assessed_controls: Record<ControlId, AssessedControl>;
	sources: SourceMin[];
	risks: Risk[];
	ai_summaries: ReportSummary[];
}

export enum TprmReportChartKind {
	ItAssessmentControlsSummaryBarPlot = "ItAssessmentControlsSummaryBarPlot",
	ItAssessmentControlsSummaryRadarPlot = "ItAssessmentControlsSummaryRadarPlot",
	ItAssessmentRisksSummaryBarPlot = "ItAssessmentRisksSummaryBarPlot",
}

export interface ReportRenderingOptions {
	target_chart?: TprmReportChartKind;
}

/**
 * All the information required to generate a report. This is sent to the
 * report renderer service.
 */
export interface ReportPayload {
	report_content?: ReportContentPayload;
	chart_content?: ChartContentPayload;
	rendering_options: ReportRenderingOptions;
}

export interface GenerateReportOpts {
	csp_nonce: string;
	payload: ReportPayload;
}

export interface GenerateTotpResponse {
	qr_code: string;
	secret: string;
}

export interface GetGraphQuery {
	selected_properties?: string[];
	filter_properties?: string[];
	filter_entity_ids?: EntityId[];
	recursion_depth?: number;
}

export interface Rational {
	num: number;
	den: number;
}

export interface GettingStartedChecklist {
	uploaded_documents: Rational;
	uploaded_historical_questionnaires: Rational;
	invited_colleagues: Rational;
	uploaded_questionnaire: Rational;
}

export interface GraphEntity {
	id: EntityId;
	properties: Record<string, IValue>;
	link_ids: EntityId[];
}

export interface GraphOperationPropertyValueRelationship {
	parent_entity_ref: GraphOperationEntityRef;
	child_entity_ref: GraphOperationEntityRef;
}

export enum GraphComponentType {
	String = "String",
	Integer = "Integer",
	Boolean = "Boolean",
	Asset = "Asset",
	Relationship = "Relationship",
}

export interface PropertyConfig {
}

export interface GraphProperty {
	property_name: string;
	label?: string;
	component_type: GraphComponentType;
	config?: PropertyConfig;
	account_id?: AccountId;
}

export interface GraphPropertyValueRelationship {
	parent_entity_id: EntityId;
	child_entity_id: EntityId;
}

export interface HistoricRiskEvaluation {
	label: string;
	color: ColorPalette;
	reason: string;
}

export interface HubspotCrmConnectionConfig {
	hubspot_account_id: string;
}

export interface IManageDocumentSourceConfig {
	domain: string;
	folder_id: string;
	folder_name: string;
	database: string;
	customer_id: string;
}

export interface IdentifiedRiskContent {
	title: string;
	description: string;
	is_predefined_risk: boolean;
	contexts: BTreeSet<RiskContextId>;
}

export interface IdentifiedSheetCell {
	row: number;
	col: number;
}

export interface IdentifiedSheetRange {
	start: IdentifiedSheetCell;
	end: IdentifiedSheetCell;
}

export interface IdentifiedSheetSection {
	section_title?: string;
	questions: IdentifiedSheetRange;
	column_headers?: IdentifiedSheetRange;
}

export interface ImportQuestions {
	questions?: CreateQuestionInSection[];
	sections?: CreateSection[];
	automate?: boolean;
}

export interface NotificationRegisteredUserMin {
	user_id: UserId;
	name: string;
	primary_email: string;
	avatar_url?: string;
}

export type NotificationPayloadType = 
	| { type: "Single", content: SingleNotificationPayload }
	| { type: "Bulk", content: BulkNotificationPayload };

export interface InAppNotificationPayload {
	action_message: string;
	actor?: NotificationRegisteredUserMin;
	payload: NotificationPayloadType;
	redirect_link: string;
}

export interface InAppNotification {
	in_app_notification_id: InAppNotificationId;
	created_at: ApiDateTime;
	read_at?: ApiDateTime;
	payload: InAppNotificationPayload;
	notification_ordinal: number;
}

export interface InAppNotificationStream {
	channel_name: string;
	number_unread: number;
}

export interface InherentRiskChangedContent {
	old_level?: number;
}

export interface InviteUser {
	name: string;
	email: string;
}

export interface LibrarySectionMin {
	library_section_id: LibrarySectionId;
	name: string;
	description?: string;
	owner?: Owner;
	parent_scope?: ScopeMin;
	parent_section_id?: LibrarySectionId;
	deleted?: Actioned;
}

export interface LinkNotificationSink {
	account_id: AccountId;
	notification_sink_id: NotificationSinkId;
	external_user_id?: string;
}

export interface NotificationSinkMin {
	notification_sink_id: NotificationSinkId;
	account_id: AccountId;
	name: string;
	config: NotificationSinkConfig;
	connector: NotificationSinkConnector;
}

export interface LinkedNotificationSinkMin {
	notification_sink: NotificationSinkMin;
	external_user_id?: string;
}

export interface ListActivitiesArgs {
	since?: number;
	before?: number;
}

export interface ListAssets {
	owner?: AssetOwner;
}

export interface ListLibrarySectionFactsArgs {
	recurse?: boolean;
}

export interface ListNotificationsArgs {
	since?: number;
	before?: number;
	limit?: number;
}

export interface ListResolutionsArgs {
	assigned_to_me?: boolean;
	limit?: number;
	offset?: number;
}

export interface ListScopeFactsArgs {
	recurse?: boolean;
}

export interface LoginCredentials {
	password: string;
	totp_code?: string;
}

export interface LoginMethod {
	login_method_id: LoginMethodId;
	name: string;
	account?: AccountMin;
	inner: LoginMethodInner;
}

export interface LoginMethodGenericOauth {
}

export interface LoginMethodGenericSaml {
	metadata_url: string;
	issuer_id: string;
}

export interface LoginRedirectRequest {
	email: string;
	redirect_url: string;
}

export type LoginRequestInner = 
	| { type: "UsernamePassword", content: UsernamePasswordLoginRequest }
	| { type: "OAuth", content: OAuthLoginRequest };

export interface LoginRequest {
	login_method_id: LoginMethodId;
	inner: LoginRequestInner;
}

export interface MailSubscription {
	mail_subscription_id: MailSubscriptionId;
	email: string;
	mailing_list_id: MailingListId;
	last_verification_attempt: ApiDateTime;
	verified_at?: ApiDateTime;
	subscribed_at?: ApiDateTime;
}

export interface SubscriberMetadata {
	name?: string;
	company?: string;
}

export interface Subscriber {
	mail_subscription_id: MailSubscriptionId;
	email: string;
	metadata: SubscriberMetadata;
	verified_at?: ApiDateTime;
	subscribed_at?: ApiDateTime;
}

export interface MailingList {
	subscribers: Subscriber[];
	active_count: number;
	inactive_count: number;
}

export interface PostmarkServerDetails {
	id?: number;
	name?: string;
}

export interface MailingListDetails {
	mailing_list_id: MailingListId;
	account_id: AccountId;
	postmark_server_details: PostmarkServerDetails;
}

export interface MentionElement {
	index: number;
}

export interface MicrosoftDocumentSourceConfig {
	drive_id: string;
	item_id: string;
	item_name: string;
}

export interface NotificationAccountMin {
	account_id: AccountId;
	display_name: string;
	slug: string;
}

export enum NotificationChannelCategory {
	ConnectedApps = "ConnectedApps",
	Email = "Email",
	EmailDigest = "EmailDigest",
	InApp = "InApp",
}

export interface NotificationPreferencesConfig {
	notification_preferences: Record<NotificationChannelCategory, HashSet<NotificationType>>;
}

export interface NotificationSink {
	notification_sink_id: NotificationSinkId;
	account_id: AccountId;
	name: string;
	config: NotificationSinkConfig;
	connector: NotificationSinkConnector;
	external_authorization?: ExternalAuthorization;
}

export interface NotionDocumentSourceConfig {
}

export interface OAuthLoginRequest {
	access_token: string;
}

export interface OperationalContext {
	operational_context_id: OperationalContextId;
	third_party_id: ThirdPartyId;
	name: string;
	description: string;
	assets: TprmAsset[];
}

export interface OriginalFile {
	file: File;
	imported_at?: ApiDateTime;
}

export interface OriginalSource {
	url: string;
	imported_at?: ApiDateTime;
}

export interface Paginated<T> {
	items: T[];
	offset: number;
	limit: number;
	total: number;
}

export interface PasswordConfig {
	length_requirement?: number;
	unique: boolean;
	random: boolean;
	characters: CharacterConfig;
	expiry?: number;
}

export enum PasswordManager {
	OnePassword = "1Password",
	LastPass = "LastPass",
	Keeper = "Keeper",
}

export enum Sso {
	GSuite = "GSuite",
	Okta = "Okta",
	AzureAD = "AzureAD",
	None = "None",
}

export interface ToolsConfig {
	password_manager: PasswordManager;
	password_manager_instructions: string;
	sso: Sso;
	sso_instructions: string;
}

export interface PasswordPolicyConfig {
	tools: ToolsConfig;
	passwords: PasswordConfig;
	exceptions: ExceptionConfig;
}

export interface PopulateRegisteredUser {
	name: string;
	email: string;
	external_ids?: Record<LoginMethodId, string>;
	password?: string;
	login_method_id?: LoginMethodId;
}

export type UserRegistrationState = 
	| { type: "Registered", content: LoginMethodMin }
	| { type: "PendingVerification", content?: undefined }
	| { type: "UnregisteredCanRegister", content?: undefined }
	| { type: "Unregistered", content?: undefined };

export interface PreLoginResponse {
	state: UserRegistrationState;
	requires_2fa: boolean;
}

export interface Property {
	property_name: string;
	value: IValue;
}

export type GraphPropertyValue = 
	| { type: "String", content: string }
	| { type: "Integer", content: number }
	| { type: "Boolean", content: boolean }
	| { type: "Asset", content: AssetId }
	| { type: "Relationship", content: GraphPropertyValueRelationship };

export interface PropertyComponent {
	entity_id: EntityId;
	property_name: string;
	value: GraphPropertyValue;
}

export interface QuestionCreatedPayload {
}

export interface QuestionGroupMin {
	question_group_id: QuestionGroupId;
	name: string;
	question_ids: QuestionId[];
}

export interface QuestionMin {
	question_id: QuestionId;
	question_number?: string;
	text: string;
}

export interface QuestionNotificationMin {
	question: QuestionMin;
	questionnaire?: QuestionnaireNotificationMin;
}

export interface QuestionSource {
	question_source_id: QuestionSourceId;
	name: string;
	connector: QuestionSourceConnector;
	config: QuestionSourceConfig;
	external_authorization: ExternalAuthorizationMin;
	external_reference_id?: ExternalAccountReferenceId;
}

export interface QuestionSourceAccountInfo {
	reference?: PopulateExternalAccountReference;
	external_name: string;
	external_icon_url: string;
}

export interface QuestionStats {
	count: number;
	in_review: number;
	completed: number;
}

export enum QuestionnaireStatus {
	Importing = "Importing",
	Automating = "Automating",
	InProgress = "InProgress",
	Complete = "Complete",
}

export interface Section {
	section_id: SectionId;
	title: string;
	description: string;
	questions: QuestionMin[];
	question_groups: QuestionGroupMin[];
}

export enum QAMode {
	Manual = "Manual",
	Automated = "Automated",
}

export interface Questionnaire {
	questionnaire_id: QuestionnaireId;
	counterparty: Counterparty;
	deal?: Deal;
	name: string;
	owner?: Owner;
	due_date?: ApiDateTime;
	status: QuestionnaireStatus;
	created_at: ApiDateTime;
	archived_at?: ApiDateTime;
	questionnaire_date?: ApiDateTime;
	question_stats: QuestionStats;
	sections: Section[];
	original_files: OriginalFile[];
	original_sources: OriginalSource[];
	scope_matrix: ScopeMatrix;
	prompt_modifiers: PromptModifiers;
	is_internal: boolean;
	internal_language_code: string;
	external_language_code: string;
	qa_mode?: QAMode;
	trust_center_visibility: TrustCenterAccess;
	deleted?: Actioned;
	access_level: AccessLevel;
}

export interface QuestionnaireMin {
	questionnaire_id: QuestionnaireId;
	counterparty: Counterparty;
	name: string;
	owner?: Owner;
	due_date?: ApiDateTime;
	status: QuestionnaireStatus;
	created_at: ApiDateTime;
	archived_at?: ApiDateTime;
	questionnaire_date?: ApiDateTime;
	question_stats: QuestionStats;
	scope_matrix: ScopeMatrix;
	prompt_modifiers: PromptModifiers;
	is_internal: boolean;
	internal_language_code: string;
	external_language_code: string;
	qa_mode?: QAMode;
	trust_center_visibility: TrustCenterAccess;
	deleted?: Actioned;
}

export interface QuestionnaireQuestionTask {
	questionnaire: QuestionnaireMin;
	question: Question;
}

export interface TprmQuestionnaireTemplateMin {
	questionnaire_template_id: TprmQuestionnaireTemplateId;
	deleted?: Actioned;
	parent_questionnaire_template_id?: TprmQuestionnaireTemplateId;
	name: string;
}

export interface TprmQuestionTemplate {
	question_template_id: TprmQuestionTemplateId;
	key?: string;
	deleted?: Actioned;
	text: string;
	guidance?: string;
	is_required: boolean;
	answer_config: TprmAnswerConfig;
	condition?: TprmCondition;
}

export interface TprmSectionTemplate {
	section_template_id: TprmSectionTemplateId;
	deleted?: Actioned;
	title: string;
	description?: string;
	questions: TprmQuestionTemplate[];
}

export interface TprmTierCriterion {
	tier: string;
	condition: TprmCondition;
}

export interface TprmQuestionnaireTemplate {
	questionnaire_template_id: TprmQuestionnaireTemplateId;
	deleted?: Actioned;
	parent_questionnaire_template?: TprmQuestionnaireTemplateMin;
	name: string;
	sections: TprmSectionTemplate[];
	tiering_criteria: TprmTierCriterion[];
	referenced_templates: Record<TprmQuestionnaireTemplateId, TprmQuestionnaireTemplate>;
}

export type TprmAnswer = 
	| { type: "Text", content: TprmTextAnswer }
	| { type: "Files", content: TprmFilesAnswer }
	| { type: "Select", content: TprmSelectAnswer }
	| { type: "SubQuestionnaires", content: TprmSubQuestionnairesAnswer };

export interface TprmResponse {
	answer: TprmAnswer;
}

export interface TprmQuestionnaire {
	questionnaire_id: TprmQuestionnaireId;
	template: TprmQuestionnaireTemplate;
	responses: Record<TprmQuestionTemplateId, TprmResponse>;
	referenced_sources: Record<SourceId, SourceMin>;
}

export interface QuestionnaireSourceKind {
	questionnaire: TprmQuestionnaire;
}

export interface TprmQuestionnaireMin {
	questionnaire_id: TprmQuestionnaireId;
	template: TprmQuestionnaireTemplateMin;
}

export interface QuestionnaireSourceMinKind {
	questionnaire: TprmQuestionnaireMin;
}

export enum RiskLevel {
	FalsePositive = "FalsePositive",
	Low = "Low",
	Medium = "Medium",
	High = "High",
}

export interface RecordBasicRiskEvaluation {
	level: RiskLevel;
	reason: string;
}

export interface RecordComponentWiseRiskEvaluation {
	components: OrderedFloatF64[];
	reason: string;
}

export interface RecordControlAssessment {
	met?: boolean;
	explanation: string;
}

export interface RecordControlAssessmentForScope {
	scope_id: TprmScopeId;
	met?: boolean;
	explanation: string;
}

export interface RecordControlEvidence {
	source_id: SourceId;
	scope_id: TprmScopeId;
	source_context: string;
}

export interface RegisteredUser {
	user_id: UserId;
	type_: UserTypeRegistered;
	name: string;
	primary_email: string;
	avatar_url?: string;
	registered_at: ApiDateTime;
	login_method?: LoginMethodMin;
	enabled_2fa: boolean;
}

export interface RelevantFact {
	fact: Fact;
	question_cosine_distance: number;
	answer_cosine_distance?: number;
}

export interface ReminderSchedule {
	days_before: number;
	hour_number: number;
	timezone: Tz;
}

export interface ReportedError {
	occurred_at: ApiDateTime;
	title: string;
	description: string;
	link_url?: string;
}

export interface RequestLoginReset {
	email: string;
	redirect?: string;
}

export interface RequestRegistration {
	name: string;
	email: string;
	redirect?: string;
}

export interface UserLoginMethod {
	login_method: LoginMethodMin;
	is_configured: boolean;
	is_recommended: boolean;
	requires_2fa: boolean;
}

export interface ResetLoginInfo {
	email: string;
	is_new_user: boolean;
	login_methods: UserLoginMethod[];
	requires_2fa: boolean;
}

export interface ResidualRiskChangedContent {
	old_level?: number;
}

export interface Resolution {
	resolution_id: ResolutionId;
	scope?: ScopeMin;
	resolution_type: ResolutionType;
	involved_facts: Fact[];
	reason: string;
	review_necessity: number;
	review_explanation: string;
	created_at: ApiDateTime;
	owner?: Owner;
	resolved?: Actioned;
	ignored?: Actioned;
	is_internal: boolean;
}

export interface ResolutionNotificationMin {
	resolution_id: ResolutionId;
	reason: string;
}

export interface ResponseLayer {
	response_yes_no?: boolean;
	response_select?: ResponseOption[];
	response_text?: string;
	status: QuestionStatus;
	due_date?: ApiDateTime;
	owner?: Owner;
	documentation_count: number;
	activity_stream: ActivityStream;
	ai_attempted: boolean;
	ai_review_comment?: string;
	ai_review_good?: boolean;
	ai_review_parts?: AnswerReviewParts;
	linked_core_response?: CoreResponse;
	reviews?: Reviewable;
	last_modified: Actioned;
}

export type ReviewActionPayload = 
	| { type: "Commented", content: string }
	| { type: "RequestReview", content: OwnerId[] }
	| { type: "UnrequestReview", content: OwnerId[] }
	| { type: "Approved", content?: string }
	| { type: "RequestChanges", content: string };

export interface ReviewPayload {
	on_behalf?: TeamId;
	action: ReviewActionPayload;
}

export interface RichTextAttributes {
	bold?: boolean;
	italic?: boolean;
}

export interface RiskAcceptanceContent {
	accepted: boolean;
}

export interface RiskContextMin {
	risk_context_id: RiskContextId;
}

export interface RiskEvaluationContent {
	level: number;
	value: number;
	components?: number[];
	reason: string;
}

export interface RiskIdPair {
	assessment_id: AssessmentId;
	risk_id: RiskId;
}

export interface RiskMin {
	risk_id: RiskId;
}

export interface RiskNode {
	risk_id: RiskId;
}

export interface RiskNotificationMin {
	assessment: AssessmentNotificationMin;
	risk_id: RiskId;
	title: string;
}

export interface RiskRelevantControlsContent {
	control_ids: BTreeSet<ControlId>;
}

export interface Role {
	role_id: RoleId;
	system_role?: SystemRole;
	name: string;
	description: string;
	member_count: number;
}

export interface SARRequest {
	name?: string;
	company?: string;
	email: string;
	address?: string;
	phone_number?: string;
	content: string;
}

export interface ScimConfig {
	scim_config_id: ScimConfigId;
	account_id: AccountId;
	name: string;
	auth_token: string;
	send_invitations: boolean;
}

export interface ScimConfigList {
	configs: ScimConfig[];
	base_url: string;
}

export interface ScopeAccess {
	owners: Owner[];
	readers: Owner[];
	writers: Owner[];
}

export interface Scope {
	scope_id: ScopeId;
	name: string;
	owner?: Owner;
	axis: ScopeAxisMin;
	access_controls?: ScopeAccess;
}

export interface SectionMin {
	section_id: SectionId;
	title: string;
	description: string;
}

export interface SendSupportEmail {
	action: string;
	subject: string;
	body: string;
	attachments: FileId[];
	cc_user: boolean;
	express?: boolean;
}

export interface ServiceUserApikey {
	user_apikey_id: UserApikeyId;
	apikey: string;
	created: Actioned;
	deleted?: Actioned;
}

export interface ServiceUserApikeyMin {
	user_apikey_id: UserApikeyId;
	created: Actioned;
	deleted?: Actioned;
}

export interface SetPassword {
	prev_credentials?: LoginCredentials;
	new_password: string;
}

export interface ShowcaseMessageIds {
	message_ids: ShowcaseUpdateMessageId[];
	total: number;
}

export interface ShowcaseUpdateMessage {
	update_message_id: ShowcaseUpdateMessageId;
	title?: string;
	content?: string;
	sent_at?: ApiDateTime;
	updated_at: ApiDateTime;
	deleted_at?: ApiDateTime;
}

export interface SlackNotificationSinkConfig {
	team_id: string;
	team_name: string;
}

export interface SlackQuestionSourceConfig {
}

export type SourceKind = 
	| { type: "Document", content: DocumentSourceKind }
	| { type: "Questionnaire", content: QuestionnaireSourceKind };

export interface Source {
	source_id: SourceId;
	name: string;
	kind: SourceKind;
	created_at: ApiDateTime;
	analyzed_at?: ApiDateTime;
	deleted?: Actioned;
	scope: TprmScopeMin;
	risk_visible: boolean;
	control_visible: boolean;
}

export interface StandaloneFile {
	standalone_file_id: StandaloneFileId;
	file: File;
	sharing_classification: SharingClassification;
}

export interface SubGraph {
	root_entity_ids: EntityId[];
	entities: Record<EntityId, GraphEntity>;
}

export interface SubscribeRequest {
	email: string;
	metadata: SubscriberMetadata;
}

export enum SystemAgentRole {
	ReviewAnswerRelevance = "ReviewAnswerRelevance",
	ReviewAnswerAccuracy = "ReviewAnswerAccuracy",
	ReviewAnswerSupportedByFact = "ReviewAnswerSupportedByFact",
	ReviewAnswerClarity = "ReviewAnswerClarity",
	ReviewAnswerCommercial = "ReviewAnswerCommercial",
}

export interface SystemAgent {
	name: string;
	description: string;
	role: SystemAgentRole;
}

export interface SystemAgentUser {
	user_id: UserId;
	type_: UserTypeSystemAgent;
	name: string;
	description: string;
	primary_email?: Never;
	avatar_url?: Never;
	role: SystemAgentRole;
}

export interface Team {
	team_id: TeamId;
	name: string;
	description?: string;
	member_count: number;
	deleted_at?: ApiDateTime;
}

export interface TeamMin {
	team_id: TeamId;
	name: string;
}

export interface TextElement {
	attributes?: RichTextAttributes;
	text: string;
}

export interface ThirdParty {
	third_party_id: ThirdPartyId;
	name: string;
	external_id?: string;
	tier?: ThirdPartyTierMin;
	tags: ThirdPartyTagMin[];
	owner?: Owner;
	created_at: ApiDateTime;
	deleted?: Actioned;
	url: string;
	due_date?: ApiDateTime;
	score: number;
	num_assessments: number;
}

export interface ThirdPartyWithQuestionnairesMin {
	third_party_id: ThirdPartyId;
	name: string;
	external_id?: string;
	tier?: ThirdPartyTierMin;
	tags: ThirdPartyTagMin[];
	owner?: Owner;
	created_at: ApiDateTime;
	deleted?: Actioned;
	url: string;
	due_date?: ApiDateTime;
	score: number;
	num_assessments: number;
	latest_created_at?: ApiDateTime;
	latest_updated_at?: ApiDateTime;
	latest_locked_at?: ApiDateTime;
}

export interface TprmAssessmentExportCompletedEventPayload {
	background_task_id: BackgroundTaskId;
	assessment_id: AssessmentId;
	file_id: FileId;
}

export interface TprmAssessmentStatusChangedEventPayload {
	assessment_id: AssessmentId;
	status: AssessmentStatus;
}

export interface TprmExternalFilesAnswer {
	file_ids: FileId[];
}

export type TprmExternalAnswer = 
	| { type: "Text", content: TprmExternalTextAnswer }
	| { type: "Files", content: TprmExternalFilesAnswer }
	| { type: "Select", content: TprmExternalSelectAnswer }
	| { type: "SubQuestionnaires", content: TprmExternalSubQuestionnairesAnswer };

export interface TprmExternalResponse {
	answer: TprmExternalAnswer;
}

export interface TprmExternalQuestionnaireMin {
	external_questionnaire_id: TprmExternalQuestionnaireId;
	data_room: DataRoom;
	parent_external_questionnaire_id?: TprmExternalQuestionnaireId;
	template: TprmQuestionnaireTemplateMin;
	label: string;
	created_at: ApiDateTime;
	updated_at?: ApiDateTime;
	locked_at?: ApiDateTime;
	submitted_at?: ApiDateTime;
	invite_key: string;
}

export interface TprmExternalQuestionnaire {
	external_questionnaire_id: TprmExternalQuestionnaireId;
	data_room: DataRoom;
	parent_external_questionnaire_id?: TprmExternalQuestionnaireId;
	label: string;
	created_at: ApiDateTime;
	updated_at?: ApiDateTime;
	locked_at?: ApiDateTime;
	submitted_at?: ApiDateTime;
	invite_key: string;
	template: TprmQuestionnaireTemplate;
	responses: Record<TprmQuestionTemplateId, TprmExternalResponse>;
	referenced_questionnaires: Record<TprmExternalQuestionnaireId, TprmExternalQuestionnaireMin>;
	referenced_files: Record<FileId, File>;
	use_latest_version: boolean;
}

/** This type is exposed to the external API. */
export interface TprmExternalQuestionnaireExternal {
	external_questionnaire_id: TprmExternalQuestionnaireId;
	parent_external_questionnaire_id?: TprmExternalQuestionnaireId;
	label: string;
	created_at: ApiDateTime;
	updated_at?: ApiDateTime;
	locked_at?: ApiDateTime;
	invite_key: string;
	template: TprmQuestionnaireTemplateMin;
	responses: Record<TprmQuestionTemplateId, TprmExternalResponse>;
	referenced_questionnaires: Record<TprmExternalQuestionnaireId, TprmExternalQuestionnaireExternal>;
	referenced_files: Record<FileId, File>;
}

export interface TprmExternalSelectAnswer {
	selected: string[];
}

export interface TprmExternalSubQuestionnairesAnswer {
	questionnaire_ids: TprmExternalQuestionnaireId[];
}

export interface TprmExternalTextAnswer {
	text: string;
}

export interface TprmFilesAnswer {
	source_ids: SourceId[];
}

export interface TprmFilesAnswerConfig {
	min_number?: number;
	max_number?: number;
}

export interface TprmReportChartCustomization {
	kind: TprmReportChartKind;
	filename: string;
}

export interface TprmScopeFilter {
	risk_visible?: boolean;
	control_visible?: boolean;
}

export interface TprmSectionTemplateMin {
	section_template_id: TprmSectionTemplateId;
	title: string;
	description?: string;
}

export interface TprmSelectAnswer {
	selected: string[];
}

export interface TprmSelectAnswerConfigOption {
	value: string;
	label: string;
}

export interface TprmSelectAnswerConfig {
	options: TprmSelectAnswerConfigOption[];
	min_number?: number;
	max_number?: number;
}

export interface TprmSubQuestionnairesAnswer {
	source_ids: SourceId[];
}

export interface TprmSubQuestionnairesAnswerConfig {
	questionnaire_template_id: TprmQuestionnaireTemplateId;
	/**
	 * On questionnaire submissino, the sub questionnaires will each be
	 * extracted to a scope of this type.
	 */
	scope_type: TprmScopeType;
}

export interface TprmTextAnswer {
	text: string;
}

export interface TprmTextAnswerConfig {
	min_length?: number;
	max_length?: number;
	/**
	 * If `true`, the answer to this question will be used as the label for the
	 * questionnaire. (e.g. "IT Application name").
	 */
	use_as_questionnaire_label?: boolean;
	is_single_line?: boolean;
}

export interface TrustCenterAiCheckItemConfig {
	title: string;
	description: string;
}

export interface TrustCenterRelatedDocumentConfig {
	document_id?: DocumentId;
}

export enum TrustCenterDataUseItemPii {
	None = "None",
	Pii = "Pii",
	SpecialCategory = "SpecialCategory",
}

export interface TrustCenterAiDataUseItemConfig {
	title: string;
	description: string;
	pii: TrustCenterDataUseItemPii;
	includes_customer_data: boolean;
	is_anonymized: boolean;
	used_for_training: boolean;
}

export interface TrustCenterAiModelProviderConfig {
	subprocessor_entity_id?: EntityId;
	models_used: string;
	purposes: string;
}

export interface TrustCenterAiConfig {
	enabled?: boolean;
	overview_content_id?: ContentId;
	related_documents?: TrustCenterRelatedDocumentConfig[];
	check_items?: TrustCenterAiCheckItemConfig[];
	data_use_items?: TrustCenterAiDataUseItemConfig[];
	model_providers?: TrustCenterAiModelProviderConfig[];
	faq_questionnaire_id?: QuestionnaireId;
}

export interface TrustCenterAuth {
	is_public: boolean;
	password?: string;
}

export interface TrustCenterBusinessInfoContentConfig {
	title?: string;
	content_id?: ContentId;
}

export interface TrustCenterHeroConfig {
	heading?: string;
	subheading?: string;
}

export enum TrustCenterTrustedPartneredType {
	Trusted = "Trusted",
	Partnered = "Partnered",
}

export interface TrustCenterTrustedByConfig {
	logo_asset_id?: AssetId;
	logo_link_url?: string;
}

export interface TrustCenterOverviewConfig {
	welcome_id?: ContentId;
	content_id?: ContentId;
	trusted_partnered?: TrustCenterTrustedPartneredType;
	trusted_by?: TrustCenterTrustedByConfig[];
}

export interface TrustCenterDataProtectionDataUseItemConfig {
	title: string;
	description: string;
	pii: TrustCenterDataUseItemPii;
	includes_customer_data: boolean;
	is_anonymized: boolean;
}

export interface TrustCenterDataProtectionOperatingCountriesConfig {
	iso_country_code: string;
	detailed_address?: string;
	description?: string;
	gdpr_europe: boolean;
	gdpr_sccs: boolean;
	gdpr_equivalency: boolean;
}

export interface TrustCenterDataProtectionConfig {
	enabled?: boolean;
	overview_content_id?: ContentId;
	related_documents?: TrustCenterRelatedDocumentConfig[];
	data_use_items?: TrustCenterDataProtectionDataUseItemConfig[];
	operating_countries?: TrustCenterDataProtectionOperatingCountriesConfig[];
	subprocessors_enabled?: boolean;
	sar_enabled?: boolean;
}

export interface TrustCenterSubscribeConfig {
	enabled?: boolean;
}

export interface TrustCenterConfigMeta {
	name?: string;
	logo_asset_id?: AssetId;
	homepage_url?: string;
	favicon_asset_id?: AssetId;
	hero?: TrustCenterHeroConfig;
	overview?: TrustCenterOverviewConfig;
	business_info?: TrustCenterBusinessInfoContentConfig[];
	ai?: TrustCenterAiConfig;
	data_protection?: TrustCenterDataProtectionConfig;
	subscribe?: TrustCenterSubscribeConfig;
	privacy_policy?: string;
}

export interface TrustCenterConfig {
	document_request_email?: string;
	theme: IObject;
	meta: TrustCenterConfigMeta;
	is_public: boolean;
}

export interface TrustCenterConfigMin {
	theme: IObject;
	meta: TrustCenterConfigMeta;
	is_public: boolean;
}

export interface TrustCenterContent {
	content_id: ContentId;
	title: string;
	content: string;
}

export interface TrustCenterContentMin {
	content_id: ContentId;
	title: string;
}

export interface TrustCenterFaqQuestion {
	question_id: QuestionId;
	text: string;
	response: string;
}

export interface TrustCenterFaqSection {
	section_id: SectionId;
	title: string;
	questions: TrustCenterFaqQuestion[];
}

export interface TrustCenterFaq {
	sections: TrustCenterFaqSection[];
}

export interface TrustCenterPasswordConfig {
	enabled?: boolean;
	password?: string;
}

export interface TrustCenterSectionConfig {
	content_id?: ContentId;
}

export interface UnsubscribeRequest {
	RecordType: string;
	ServerID: number;
	Recipient: string;
}

export interface UpdateCrmConnection {
	external_authorization_id?: ExternalAuthorizationId;
	config: CrmConnectionConfig;
}

export interface UpdateDocumentContent {
	authority: DocumentAuthority;
	file_id?: FileId;
	markdown?: string;
	authoring_config?: Config;
	authoring_template?: string;
}

export interface UpdateDocumentSource {
	name: string;
	external_authorization_id?: ExternalAuthorizationId;
	config: DocumentSourceConfig;
	default_owner_id: OwnerId;
	default_sharing_classification: SharingClassification;
	default_limited_to_scope_ids?: ScopeId[];
}

export interface UpdateEsignatureProvider {
	external_authorization_id?: ExternalAuthorizationId;
	external_reference?: PopulateExternalAccountReference;
	config: EsignatureProviderConfig;
}

export interface UpdateLibrarySection {
	name?: string;
	description?: string;
}

export enum NotificationType {
	AddedToTeam = "AddedToTeam",
	RemovedFromTeam = "RemovedFromTeam",
	AdminAdded = "AdminAdded",
	AdminRemoved = "AdminRemoved",
	AddedAsAdmin = "AddedAsAdmin",
	RemovedAsAdmin = "RemovedAsAdmin",
	QuestionAssigned = "QuestionAssigned",
	QuestionCommented = "QuestionCommented",
	QuestionFinishedAutomating = "QuestionFinishedAutomating",
	QuestionMentioned = "QuestionMentioned",
	QuestionUpdated = "QuestionUpdated",
	QuestionDueSoon = "QuestionDueSoon",
	QuestionReviewRequested = "QuestionReviewRequested",
	QuestionReviewCommented = "QuestionReviewCommented",
	QuestionReviewChangesRequested = "QuestionReviewChangesRequested",
	QuestionReviewApproved = "QuestionReviewApproved",
	QuestionnaireAssigned = "QuestionnaireAssigned",
	QuestionnaireCommented = "QuestionnaireCommented",
	QuestionnaireFinishedAutomating = "QuestionnaireFinishedAutomating",
	QuestionnaireMentioned = "QuestionnaireMentioned",
	QuestionnaireUpdated = "QuestionnaireUpdated",
	QuestionnaireDueSoon = "QuestionnaireDueSoon",
	DocumentAssigned = "DocumentAssigned",
	DocumentCommented = "DocumentCommented",
	DocumentMentioned = "DocumentMentioned",
	DocumentUpdated = "DocumentUpdated",
	ReviewItemAssigned = "ReviewItemAssigned",
	ControlMentioned = "ControlMentioned",
	ControlCommented = "ControlCommented",
	ControlReviewRequested = "ControlReviewRequested",
	ControlReviewCommented = "ControlReviewCommented",
	ControlReviewChangesRequested = "ControlReviewChangesRequested",
	ControlReviewApproved = "ControlReviewApproved",
	RiskAssigned = "RiskAssigned",
	RiskMentioned = "RiskMentioned",
	RiskCommented = "RiskCommented",
	RiskReviewRequested = "RiskReviewRequested",
	RiskReviewCommented = "RiskReviewCommented",
	RiskReviewChangesRequested = "RiskReviewChangesRequested",
	RiskReviewApproved = "RiskReviewApproved",
	AssessmentFinishedAutomating = "AssessmentFinishedAutomating",
	AssessmentAssigned = "AssessmentAssigned",
	ThirdPartyAssigned = "ThirdPartyAssigned",
	ThirdPartyQuestionnaireSubmitted = "ThirdPartyQuestionnaireSubmitted",
}

export interface UpdateNotificationPreference {
	notification_type: NotificationType;
	channel_category: NotificationChannelCategory;
	enabled: boolean;
}

export interface UpdateNotificationSink {
	external_authorization_id?: ExternalAuthorizationId;
	config: NotificationSinkConfig;
}

export interface UpdateOperationalContext {
	name?: string;
	description?: string;
}

export interface UpdateQuestionSource {
	external_authorization_id: ExternalAuthorizationId;
	config: QuestionSourceConfig;
}

export interface UpdateScopeAccess {
	writer_ids: OwnerId[];
	reader_ids: OwnerId[];
}

export interface UpdateTprmAsset {
	name?: string;
	description?: string;
}

export type EnableTwoFa = 
	| { type: "Totp", content: EnableTwoFaTotp };

export interface UpdateTwoFa {
	credentials: LoginCredentials;
	enable?: EnableTwoFa;
}

export interface UpdateWatcherTrigger {
	trigger: WatcherTrigger;
	enabled: boolean;
}

export interface UploadOptions {
	system_wide?: boolean;
}

export interface UsernamePasswordLoginRequest {
	email: string;
	remember_me: boolean;
	credentials: LoginCredentials;
}

export type ValidateLoginMethodInner = 
	| { type: "GenericOauth", content: ValidateLoginMethodGenericOauth }
	| { type: "GenericSaml", content: ValidateLoginMethodGenericSaml };

export interface ValidateLoginMethod {
	name: string;
	inner: ValidateLoginMethodInner;
}

export interface ValidateLoginMethodGenericOauth {
}

export interface ValidateLoginMethodGenericSaml {
	metadata_url: string;
}

export interface VerifiedDomain {
	verified_domain_id: VerifiedDomainId;
	domain: string;
	verified?: Actioned;
	enable_self_enrollment: boolean;
}

export interface WebDocumentSourceConfig {
	urls: string[];
}

export interface WhoAmIOnAccountResponse {
	user_owner?: Owner;
	associated_owners: Owner[];
	account: Account;
	apikey?: Apikey;
	roles: RoleMin[];
}

/** Contains information about the user. Does not contain account-specific information. */
export interface WhoAmIResponse {
	user: RegisteredUser;
	account?: AccountMin;
	multi_account: boolean;
	internal_mode: boolean;
	used_2fa: boolean;
}

export enum AIProvider {
	OpenAI = "OpenAI",
	VertexAI = "VertexAI",
	AnthropicViaVertexAI = "AnthropicViaVertexAI",
}

export type AddLoginMethod = 
	| { type: "UsernamePassword", content: SetPassword }
	| { type: "OAuth", content: string };

export enum AiStatus {
	NotAttempted = "NotAttempted",
	NoAnswer = "NoAnswer",
	RejectedAnswer = "RejectedAnswer",
	AcceptedAnswer = "AcceptedAnswer",
}

export type AssessedControlUpdatedPayload = 
	| { type: "Added", content?: undefined }
	| { type: "SelectedScopes", content: TprmScopeId[] }
	| { type: "MetForScope", content: RecordControlAssessmentForScope }
	| { type: "Met", content: RecordControlAssessment }
	| { type: "RelevantRiskLinked", content: RiskId }
	| { type: "RelevantRiskUnlinked", content: RiskId }
	| { type: "EvidenceAdded", content: ControlEvidenceId }
	| { type: "EvidenceRemoved", content: ControlEvidenceId };

export enum AssessmentDecisionNodeType {
	SourceData = "SourceData",
	ControlScopeSelection = "ControlScopeSelection",
	ControlScopeAssessment = "ControlScopeAssessment",
	ControlAssessment = "ControlAssessment",
	IdentifiedRisks = "IdentifiedRisks",
	IdentifiedRisk = "IdentifiedRisk",
	InherentRisk = "InherentRisk",
	RiskRelevantControls = "RiskRelevantControls",
	ResidualRisk = "ResidualRisk",
	RiskAcceptance = "RiskAcceptance",
}

/** The frontend defines its own types for these so we can use typebox to parse the task result */
export type BackgroundTask = 
	| { type: "Pending", content: PendingBackgroundTask }
	| { type: "Complete", content: CompleteBackgroundTask };

export type BulkNotificationPayload = 
	| { type: "Team", content: number }
	| { type: "User", content: number }
	| { type: "Question", content: BatchQuestionNotificationMin }
	| { type: "Questionnaire", content: BatchQuestionnaireNotificationMin }
	| { type: "Document", content: BatchDocumentNotificationMin }
	| { type: "ReviewItem", content: number }
	| { type: "Assessment", content: BatchAssessmentNotificationMin }
	| { type: "ThirdParty", content: number }
	| { type: "Control", content: BatchControlNotificationMin }
	| { type: "Risk", content: BatchRiskNotificationMin };

export type DocumentUpdatedPayload = 
	| { type: "Created", content?: undefined }
	| { type: "Name", content: string }
	| { type: "Category", content: DocumentCategory }
	| { type: "Scopes", content?: undefined }
	| { type: "SharingClassification", content: SharingClassification }
	| { type: "Owner", content?: undefined }
	| { type: "ReviewPeriod", content?: undefined }
	| { type: "TrustCenterConfig", content?: undefined }
	| { type: "File", content?: undefined }
	| { type: "AiText", content?: undefined }
	| { type: "Deleted", content?: undefined }
	| { type: "Restored", content?: undefined }
	| { type: "Generic", content?: undefined };

export enum ExternalAuthorizationType {
	Oauth = "Oauth",
	Raw = "Raw",
}

export enum FeedEventType {
	TprmAssessmentStatusChanged = "TprmAssessmentStatusChanged",
	TprmAssessmentExportCompleted = "TprmAssessmentExportCompleted",
}

export type GraphOperation = 
	| { op: "CreateEntity", content?: undefined }
	| { op: "DeleteEntity", content: {
	entity_ref: GraphOperationEntityRef;
	recursive?: boolean;
}}
	| { op: "UpsertProperty", content: {
	name: string;
	entity_ref: GraphOperationEntityRef;
	value: GraphOperationPropertyValue;
}}
	| { op: "DeleteProperty", content: {
	name: string;
	entity_ref: GraphOperationEntityRef;
}};

export type GraphOperationPropertyValue = 
	| { type: "String", content: string }
	| { type: "Integer", content: number }
	| { type: "Boolean", content: boolean }
	| { type: "Asset", content: AssetId }
	| { type: "Relationship", content: GraphOperationPropertyValueRelationship };

export type GraphOperationResponse = 
	| { op: "CreateEntity", content: {
	entity_id: EntityId;
}}
	| { op: "DeleteEntity", content: {
	entity_ids: EntityId[];
}}
	| { op: "UpsertProperty", content: {
	existed: boolean;
}}
	| { op: "DeleteProperty", content: {
	existed: boolean;
}};

export type Invalidation = 
	| { type: "Question", content: QuestionId }
	| { type: "Questionnaire", content: QuestionnaireId };

export enum InvalidationCauseType {
	New = "New",
	SourcesChanged = "SourcesChanged",
	RiskIdentified = "RiskIdentified",
	RiskUpdated = "RiskUpdated",
	InherentRiskChanged = "InherentRiskChanged",
	ControlRelevanceChanged = "ControlRelevanceChanged",
	ControlMetChanged = "ControlMetChanged",
	ResidualRiskChanged = "ResidualRiskChanged",
	ControlScopeSelectionChanged = "ControlScopeSelectionChanged",
	ControlScopeMetChanged = "ControlScopeMetChanged",
}

export enum LoginMethodType {
	UsernamePassword = "UsernamePassword",
	GoogleOAuth = "GoogleOAuth",
	MicrosoftOAuth = "MicrosoftOAuth",
	GenericOauth = "GenericOauth",
	GenericSaml = "GenericSaml",
}

export enum NotificationChannelType {
	Slack = "Slack",
	Email = "Email",
	EmailDigest = "EmailDigest",
	InApp = "InApp",
}

export enum NotificationStatus {
	Read = "Read",
	Unread = "Unread",
	All = "All",
}

export enum OAuthRedirectTarget {
	Google = "google",
	Atlassian = "atlassian",
	Hubspot = "hubspot",
	Slack = "slack",
	MicrosoftSpa = "microsoft-spa",
	MicrosoftWeb = "microsoft-web",
	Docusign = "docusign",
	Notion = "notion",
	IManage = "imanage",
}

export enum OneTimeTokenType {
	Invite = "Invite",
	PasswordReset = "PasswordReset",
	SubscriptionVerification = "SubscriptionVerification",
}

export enum OwnerType {
	User = "User",
	Team = "Team",
}

export enum PovModifier {
	FirstPerson = "FirstPerson",
	ThirdPerson = "ThirdPerson",
}

export enum ProductType {
	VendorToolkit = "VendorToolkit",
	TPRM = "TPRM",
	All = "All",
}

export type PromptModifier = 
	| { type: "Verbosity", content: VerbosityModifier }
	| { type: "Pov", content: PovModifier }
	| { type: "Custom", content: string };

export enum PromptModifierType {
	Verbosity = "Verbosity",
	Pov = "Pov",
	Custom = "Custom",
}

export type QuestionUpdatedPayload = 
	| { type: "Text", content?: undefined }
	| { type: "Parts", content?: undefined }
	| { type: "DueDate", content?: ApiDateTime }
	| { type: "Owner", content?: undefined }
	| { type: "Status", content: QuestionStatus }
	| { type: "Response", content?: undefined }
	| { type: "Scopes", content?: undefined }
	| { type: "Generic", content?: undefined };

export type RecordRiskEvaluation = 
	| { type: "Basic", content: RecordBasicRiskEvaluation }
	| { type: "ComponentWise", content: RecordComponentWiseRiskEvaluation };

export type ResolveOwner = 
	| { type: "Team", content: TeamId }
	| { type: "User", content: UserId };

export type RiskUpdatedPayload = 
	| { type: "Created", content?: undefined }
	| { type: "Title", content: string }
	| { type: "Description", content: string }
	| { type: "InherentLevel", content: HistoricRiskEvaluation }
	| { type: "ResidualLevel", content: HistoricRiskEvaluation }
	| { type: "Accepted", content: boolean }
	| { type: "RelevantControlLinked", content: ControlId }
	| { type: "RelevantControlUnlinked", content: ControlId }
	| { type: "Owner", content?: undefined }
	| { type: "ContextAdded", content: RiskContextId }
	| { type: "ContextRemoved", content: RiskContextId };

export type SingleNotificationPayload = 
	| { type: "Team", content: TeamMin }
	| { type: "User", content: UserMin }
	| { type: "Question", content: QuestionNotificationMin }
	| { type: "Questionnaire", content: QuestionnaireNotificationMin }
	| { type: "Document", content: DocumentNotificationMin }
	| { type: "ReviewItem", content: ResolutionNotificationMin }
	| { type: "Assessment", content: AssessmentNotificationMin }
	| { type: "ThirdParty", content: ThirdPartyNotificationMin }
	| { type: "Control", content: ControlNotificationMin }
	| { type: "Risk", content: RiskNotificationMin }
	| { type: "ThirdPartyQuestionnaire", content: ThirdPartyNotificationMin };

export enum SourceUsageType {
	Risk = "Risk",
	Control = "Control",
}

export type SourcedFact = 
	| { type: "Fact", content: Fact }
	| { type: "FactText", content: FactTextMin };

export enum SubscriptionAction {
	Verification = "Verification",
	AlreadySubscribed = "AlreadySubscribed",
	Resubscribe = "Resubscribe",
	RateLimited = "RateLimited",
}

export enum SystemAgentDomain {
	ReviewAnswer = "ReviewAnswer",
}

export type Task = 
	| { type: "QuestionnaireQuestion", content: QuestionnaireQuestionTask }
	| { type: "AdHocQuestion", content: AdHocQuestionTask };

/** This is serialized as JSON to the database, so be careful with changes. */
export enum TprmConditionComparisonOperator {
	Equal = "Equal",
	NotEqual = "NotEqual",
	LessThan = "LessThan",
	LessThanOrEqual = "LessThanOrEqual",
	GreaterThan = "GreaterThan",
	GreaterThanOrEqual = "GreaterThanOrEqual",
}

/** This is serialized as JSON to the database, so be careful with changes. */
export enum TprmConditionSelectMatchesCriteria {
	/** Answer exactly matches the condition values (sets are equal) */
	Exact = "Exact",
	/** Answer contains all of the condition values (answer values are a superset of condition values) */
	ContainsAll = "ContainsAll",
	/** Answer contains only the condition values (answer values are a subset of condition values) */
	ContainsOnly = "ContainsOnly",
	/** Answer contains any of the condition values (answer values and condition values are overlapping) */
	ContainsAny = "ContainsAny",
	/** Answer does not contain any of the condition values (answer values and condition values are disjoint) */
	NotContainsAny = "NotContainsAny",
}

export enum TprmScopeVisibility {
	Risk = "Risk",
	Control = "Control",
	All = "All",
}

export enum UpdateNotificationStatus {
	New = "New",
	Draft = "Draft",
	Sent = "Sent",
}

export enum VerbosityModifier {
	Descriptive = "Descriptive",
	Balanced = "Balanced",
	Concise = "Concise",
}

